/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
    Autocomplete,
  Badge,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Paper,
  TablePagination,
  Chip,
  Button,
  CircularProgress,
  Tooltip,
  Collapse,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close,
  Search,
  ExpandMore,
  ExpandLess,
  Edit,
  Delete,
  LocationOn,
  Download,
  FilterList,
  Clear,
  SupportAgent,
  Mic,
  Videocam,
  Visibility,
  PlayCircle,
  CheckCircle,
  Warning,
  History,
  Photo,
  Description,
  ChatBubbleOutline,
  Send,
  ThumbUp,
  Pause,
  PlayArrow,
  Map as MapIcon,
  Place,
  Archive,
  Update,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import OcorrenciasChamadosTab from '../components/OcorrenciasChamadosTab';
import PlantaViewer from '../components/PlantaViewer';
import SignaturePadField from '../components/SignaturePadField';
import { jsPDF } from 'jspdf';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'aberto', label: 'Aberto', color: 'info' },
  { value: 'em_andamento', label: 'Em Andamento', color: 'warning' },
  { value: 'aguardando_resposta', label: 'Aguardando Resposta', color: 'secondary' },
  { value: 'pausado', label: 'Pausado', color: 'default' },
  { value: 'resolvido', label: 'Resolvido', color: 'success' },
  { value: 'fechado', label: 'Fechado', color: 'error' },
];

const PRIORIDADE_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'baixa', label: 'Baixa', color: 'success' },
  { value: 'media', label: 'Média', color: 'info' },
  { value: 'alta', label: 'Alta', color: 'warning' },
  { value: 'urgente', label: 'Urgente', color: 'error' },
];

const TIPO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'emergencia', label: 'Emergência' },
  { value: 'suporte', label: 'Suporte Técnico' },
  { value: 'solicitacao', label: 'Solicitação' },
  { value: 'reclamacao', label: 'Reclamação' },
  { value: 'duvida', label: 'Dúvida' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'reforma', label: 'Reforma' },
  { value: 'obra', label: 'Obra' },
  { value: 'projetos', label: 'Projetos' },
  { value: 'outro', label: 'Outro' },
];

const WHATSAPP_MEDIA_KIND_LABEL = {
  image: 'Foto',
  video: 'Vídeo',
  audio: 'Áudio',
  document: 'Arquivo',
};

const EMERGENCIA_LEMBRAR_DEPOIS_MS = 120000;

const normalizeWhatsappMediaList = (media) => {
  if (!Array.isArray(media)) return [];
  return media.filter((entry) => entry && typeof entry === 'object');
};

const filterWhatsappMediaByKind = (media, kind) =>
  normalizeWhatsappMediaList(media).filter((entry) => entry.kind === kind);

const resolveWhatsappMediaUrl = (value) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (typeof window === 'undefined') return value;

  try {
    return new URL(value, window.location.origin).toString();
  } catch {
    return value;
  }
};

const formatWhatsappMediaSize = (value) => {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const isWhatsappMonitorObservacao = (value) => {
  const text = value?.toString() || '';
  return (
    text.includes('Pré-preenchido a partir do Monitor do Agente WhatsApp') ||
    text.includes('Mídia do WhatsApp:') ||
    text.includes('Tipo da mensagem:')
  );
};

const inferWhatsappMediaKindFromUrl = (value) => {
  const url = value?.toString().trim().toLowerCase() || '';

  if (/(\.jpg|\.jpeg|\.png|\.gif|\.webp|\.bmp)(\?|$)/.test(url)) return 'image';
  if (/(\.mp4|\.webm|\.mov|\.m4v|\.avi|\.mkv)(\?|$)/.test(url)) return 'video';
  if (/(\.mp3|\.wav|\.ogg|\.m4a|\.aac|\.opus|\.webm)(\?|$)/.test(url)) return 'audio';
  return 'document';
};

const extractWhatsappMediaFromObservacoes = (value) => {
  if (!isWhatsappMonitorObservacao(value)) return [];

  const urls = Array.from(new Set((value.match(/https?:\/\/\S+/g) || []).map((item) => item.trim())));
  return urls.map((url, index) => ({
    kind: inferWhatsappMediaKindFromUrl(url),
    url,
    file_name: `Mídia WhatsApp ${index + 1}`,
  }));
};

const getChamadoWhatsappMediaList = (chamado) => {
  const directMedia = normalizeWhatsappMediaList(chamado?.whatsapp_media);
  if (directMedia.length > 0) return directMedia;
  return extractWhatsappMediaFromObservacoes(chamado?.observacoes);
};

const resolveChamadoCreatorName = (chamado, usuariosById = null) => {
  const directName = chamado?.criador_nome || chamado?.nome_usuario;
  if (directName) return directName;

  const creatorId = chamado?.criado_por;
  if (creatorId !== null && creatorId !== undefined && creatorId !== '') {
    const usuario = usuariosById?.get?.(String(creatorId));
    return usuario?.nome || usuario?.username || String(creatorId);
  }

  return '-';
};

const EMPTY_CLOSING_REPORT = Object.freeze({
  tecnico_responsavel: '',
  acompanhou_servico: '',
  local_atendimento: '',
  problema_reportado: '',
  diagnostico: '',
  servico_executado: '',
  materiais_utilizados: '',
  orientacoes_cliente: '',
  pendencias_observacoes: '',
  conclusao: '',
  equipamento_liberado: true,
  retorno_necessario: false,
  cliente_ciente: true,
  nome_assinante: '',
  cargo_assinante: '',
  assinatura: '',
});

const CLOSING_REPORT_REQUIRED_FIELDS = [
  { key: 'tecnico_responsavel', label: 'Responsável técnico' },
  { key: 'diagnostico', label: 'Diagnóstico realizado' },
  { key: 'servico_executado', label: 'Serviço executado' },
  { key: 'conclusao', label: 'Conclusão do atendimento' },
  { key: 'nome_assinante', label: 'Nome do assinante' },
  { key: 'assinatura', label: 'Assinatura na tela' },
];

const getEmptyClosingReport = () => ({ ...EMPTY_CLOSING_REPORT });

const normalizeClosingReport = (value) => {
  if (!value) return getEmptyClosingReport();

  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return getEmptyClosingReport();
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return getEmptyClosingReport();
  }

  return {
    ...getEmptyClosingReport(),
    ...parsed,
    equipamento_liberado: parsed.equipamento_liberado !== undefined ? Boolean(parsed.equipamento_liberado) : true,
    retorno_necessario: Boolean(parsed.retorno_necessario),
    cliente_ciente: parsed.cliente_ciente !== undefined ? Boolean(parsed.cliente_ciente) : true,
  };
};

const serializeClosingReport = (value) => {
  const report = normalizeClosingReport(value);
  const normalized = Object.fromEntries(
    Object.entries(report).map(([key, currentValue]) => {
      if (typeof currentValue === 'string') {
        return [key, currentValue.trim()];
      }

      return [key, Boolean(currentValue)];
    })
  );

  return JSON.stringify(normalized);
};

const hasClosingReportContent = (value) => {
  const report = normalizeClosingReport(value);
  return [
    report.diagnostico,
    report.servico_executado,
    report.conclusao,
    report.materiais_utilizados,
    report.orientacoes_cliente,
    report.pendencias_observacoes,
    report.nome_assinante,
    report.assinatura,
  ].some((entry) => String(entry || '').trim());
};

const buildClosingReportDraft = (chamado, user) => {
  const existing = normalizeClosingReport(chamado?.relatorio_fechamento);
  const tecnicoPadrao = chamado?.atendente || user?.nome || user?.username || '';
  const acompanhantePadrao = chamado?.nome_usuario || '';
  const pendencias = existing.pendencias_observacoes || chamado?.pendencia || '';

  return {
    ...existing,
    tecnico_responsavel: existing.tecnico_responsavel || tecnicoPadrao,
    acompanhou_servico: existing.acompanhou_servico || acompanhantePadrao,
    local_atendimento: existing.local_atendimento || chamado?.cliente_nome || '',
    problema_reportado: existing.problema_reportado || chamado?.descricao || chamado?.titulo || '',
    nome_assinante: existing.nome_assinante || acompanhantePadrao,
    pendencias_observacoes: pendencias,
    retorno_necessario: existing.retorno_necessario || Boolean(pendencias),
  };
};

const getClosingReportMissingFields = (value) => {
  const report = normalizeClosingReport(value);

  return CLOSING_REPORT_REQUIRED_FIELDS.filter(({ key }) => !String(report[key] || '').trim()).map(
    ({ label }) => label
  );
};

function ChamadoClosingReportSection({ report }) {
  const closingReport = normalizeClosingReport(report);

  if (!hasClosingReportContent(closingReport)) {
    return null;
  }

  const identificationFields = [
    { label: 'Responsável técnico', value: closingReport.tecnico_responsavel },
    { label: 'Acompanhou o serviço', value: closingReport.acompanhou_servico },
    { label: 'Local do atendimento', value: closingReport.local_atendimento },
    { label: 'Assinante', value: closingReport.nome_assinante },
    { label: 'Cargo/Função', value: closingReport.cargo_assinante },
  ].filter((item) => item.value);

  const executionFields = [
    { label: 'Problema reportado', value: closingReport.problema_reportado },
    { label: 'Diagnóstico realizado', value: closingReport.diagnostico },
    { label: 'Serviço executado', value: closingReport.servico_executado },
    { label: 'Materiais utilizados', value: closingReport.materiais_utilizados },
    { label: 'Orientações ao cliente', value: closingReport.orientacoes_cliente },
    { label: 'Pendências e observações finais', value: closingReport.pendencias_observacoes },
    { label: 'Conclusão', value: closingReport.conclusao },
  ].filter((item) => item.value);

  const statusItems = [
    {
      label: closingReport.equipamento_liberado ? 'Equipamento liberado' : 'Equipamento não liberado',
      color: closingReport.equipamento_liberado ? 'success' : 'default',
      variant: 'filled',
    },
    {
      label: closingReport.retorno_necessario ? 'Retorno necessário' : 'Sem retorno previsto',
      color: closingReport.retorno_necessario ? 'warning' : 'success',
      variant: closingReport.retorno_necessario ? 'filled' : 'outlined',
    },
    {
      label: closingReport.cliente_ciente ? 'Cliente ciente' : 'Cliente não validado',
      color: closingReport.cliente_ciente ? 'info' : 'default',
      variant: closingReport.cliente_ciente ? 'filled' : 'outlined',
    },
  ];

  return (
    <Paper sx={{ p: 0, bgcolor: '#f8fafc', border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, bgcolor: '#e2e8f0', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Relatório de Fechamento
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Registro consolidado do atendimento executado e do aceite no encerramento.
        </Typography>
      </Box>

      <Stack spacing={2.5} sx={{ p: 2 }}>
        {identificationFields.length > 0 && (
          <Box>
            <Typography variant="overline" sx={{ color: '#b45309', fontWeight: 700 }}>
              Identificação
            </Typography>
            <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
              {identificationFields.map((item) => (
                <Grid item xs={12} sm={6} md={item.label === 'Local do atendimento' ? 12 : 6} key={item.label}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: '#fff' }}>
                    <Typography variant="caption" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.4, whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                      {item.value}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box>
          <Typography variant="overline" sx={{ color: '#b45309', fontWeight: 700 }}>
            Validação do encerramento
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            {statusItems.map((item) => (
              <Chip key={item.label} label={item.label} color={item.color} size="small" variant={item.variant} />
            ))}
          </Stack>
        </Box>

        <Box>
          <Typography variant="overline" sx={{ color: '#b45309', fontWeight: 700 }}>
            Execução do atendimento
          </Typography>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            {executionFields.map((item) => (
              <Grid item xs={12} md={item.label === 'Problema reportado' ? 12 : 6} key={item.label}>
                <Paper variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: '#fff' }}>
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {item.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {closingReport.assinatura && (
          <Box>
            <Typography variant="overline" sx={{ color: '#b45309', fontWeight: 700 }}>
              Assinatura e aceite
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: '#fff' }}>
              <Stack spacing={1.25}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Assinante
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {closingReport.nome_assinante || '-'}
                    {closingReport.cargo_assinante ? ` • ${closingReport.cargo_assinante}` : ''}
                  </Typography>
                </Box>
                <Box
                  component="img"
                  src={closingReport.assinatura}
                  alt="Assinatura do fechamento do chamado"
                  sx={{
                    width: '100%',
                    maxWidth: 360,
                    bgcolor: '#fff',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                />
              </Stack>
            </Paper>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function WhatsappChamadoMediaSection({ chamado, compact = false }) {
  const mediaItems = getChamadoWhatsappMediaList(chamado);

  if (mediaItems.length === 0) return null;

  const imageItems = filterWhatsappMediaByKind(mediaItems, 'image');
  const videoItems = filterWhatsappMediaByKind(mediaItems, 'video');
  const audioItems = filterWhatsappMediaByKind(mediaItems, 'audio');
  const documentItems = filterWhatsappMediaByKind(mediaItems, 'document');
  const unavailableItems = mediaItems.filter(
    (entry) => !resolveWhatsappMediaUrl(entry.url) && entry.storage_error
  );

  const imageGrid = compact ? { xs: 6, sm: 4 } : { xs: 4, sm: 3, md: 2 };
  const videoGrid = compact ? { xs: 12, sm: 6 } : { xs: 6, sm: 4 };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Mídias do WhatsApp
      </Typography>
      <Stack spacing={1.5}>
        {imageItems.length > 0 ? (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Fotos
            </Typography>
            <Grid container spacing={1}>
              {imageItems.map((media, index) => {
                const mediaUrl = resolveWhatsappMediaUrl(media.url);
                const label = media.file_name || `Foto ${index + 1}`;

                return (
                  <Grid item key={`${label}-${index}`} {...imageGrid}>
                    <Card sx={{ cursor: 'pointer' }} onClick={() => window.open(mediaUrl, '_blank')}>
                      <CardMedia
                        component="img"
                        height={compact ? 90 : 80}
                        image={mediaUrl}
                        alt={label}
                        sx={{ objectFit: 'cover' }}
                      />
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ) : null}

        {videoItems.length > 0 ? (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Vídeos
            </Typography>
            <Grid container spacing={1}>
              {videoItems.map((media, index) => {
                const mediaUrl = resolveWhatsappMediaUrl(media.url);
                const sizeLabel = formatWhatsappMediaSize(media.byte_size);

                return (
                  <Grid item key={`${media.file_name || 'video'}-${index}`} {...videoGrid}>
                    <Stack spacing={0.5}>
                      <video src={mediaUrl} controls style={{ width: '100%', borderRadius: 4 }} />
                      {media.file_name || sizeLabel ? (
                        <Typography variant="caption" color="text.secondary">
                          {[media.file_name, sizeLabel].filter(Boolean).join(' • ')}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ) : null}

        {audioItems.length > 0 ? (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Áudios
            </Typography>
            <Stack spacing={1}>
              {audioItems.map((media, index) => {
                const mediaUrl = resolveWhatsappMediaUrl(media.url);
                const sizeLabel = formatWhatsappMediaSize(media.byte_size);

                return (
                  <Paper key={`${media.file_name || 'audio'}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={1}>
                      {(media.file_name || sizeLabel) && (
                        <Typography variant="caption" color="text.secondary">
                          {[media.file_name || `Áudio ${index + 1}`, sizeLabel]
                            .filter(Boolean)
                            .join(' • ')}
                        </Typography>
                      )}
                      <audio controls src={mediaUrl} style={{ width: '100%' }} />
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        ) : null}

        {documentItems.length > 0 ? (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Arquivos
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {documentItems.map((media, index) => {
                const mediaUrl = resolveWhatsappMediaUrl(media.url);
                const sizeLabel = formatWhatsappMediaSize(media.byte_size);
                const label =
                  [media.file_name || `Arquivo ${index + 1}`, sizeLabel].filter(Boolean).join(' • ') ||
                  `Arquivo ${index + 1}`;

                return (
                  <Chip
                    key={`${media.file_name || 'documento'}-${index}`}
                    label={label}
                    icon={<Description />}
                    variant="outlined"
                    onClick={() => window.open(mediaUrl, '_blank')}
                    sx={{ mb: 1 }}
                  />
                );
              })}
            </Stack>
          </Box>
        ) : null}

        {unavailableItems.map((media, index) => (
          <Alert key={`whatsapp-media-warning-${index}`} severity="warning" variant="outlined">
            {WHATSAPP_MEDIA_KIND_LABEL[media.kind] || 'Mídia'} identificada, mas indisponível: {media.storage_error}
          </Alert>
        ))}
      </Stack>
    </Box>
  );
}

function ChamadoFotoThumb({ chamadoId, url, token, onOpen }) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!chamadoId || !url) return undefined;

    let active = true;
    let objectUrl = '';
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    fetch(`/api/chamados/${chamadoId}/anexo?path=${encodeURIComponent(url)}`, { headers: authHeaders })
      .then((response) => {
        if (!response.ok) throw new Error('Falha ao carregar foto');
        return response.blob();
      })
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [chamadoId, token, url]);

  return (
    <Card
      onClick={() => onOpen(url)}
      title="Clique para ampliar"
      sx={{
        width: 70,
        height: 70,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'zoom-in',
        border: '1px solid rgba(25, 118, 210, 0.35)',
        bgcolor: 'grey.100',
        '&:hover': { boxShadow: 4, transform: 'scale(1.03)' },
      }}
    >
      {src && !failed ? (
        <CardMedia component="img" height="70" image={src} sx={{ objectFit: 'cover' }} />
      ) : (
        <Box sx={{ height: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'text.secondary', textAlign: 'center', px: 0.5 }}>
          {failed ? 'Erro' : 'Carregando'}
        </Box>
      )}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          color: '#fff',
          bgcolor: 'rgba(0,0,0,0.62)',
          fontSize: 9,
          lineHeight: '16px',
          textAlign: 'center',
          fontWeight: 700,
        }}
      >
        AMPLIAR
      </Box>
    </Card>
  );
}

export default function ConsultaChamadosModal({ open, onClose }) {
  const { token, user, hasPermission } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [chamados, setChamados] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [metricas, setMetricas] = useState(null);

  // Filtros
  const [clienteId, setClienteId] = useState('');
  const [numeroChamado, setNumeroChamado] = useState('');
  const [numeroChamadoOptions, setNumeroChamadoOptions] = useState([]);
  const [status, setStatus] = useState('');
  const [prioridade, setPrioridade] = useState('');
  const [tipo, setTipo] = useState('');
  const [busca, setBusca] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [prazoInicio, setPrazoInicio] = useState('');
  const [prazoFim, setPrazoFim] = useState('');
  const [prazoVencido, setPrazoVencido] = useState(false);

  // Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Detalhes expandidos
  const [expandedRow, setExpandedRow] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [chatChamado, setChatChamado] = useState(null);
  const [novoComentarioChat, setNovoComentarioChat] = useState('');
  const [chatSubmitting, setChatSubmitting] = useState(false);
  const [comentariosLidosPorChamado, setComentariosLidosPorChamado] = useState({});

  // Modal de visualização
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [chamadoDetalhes, setChamadoDetalhes] = useState(null);

  // Modal de edição
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [chamadoEditando, setChamadoEditando] = useState(null);
  
  // Arquivos adicionais na edição
  const [editFotos, setEditFotos] = useState([]);
  const [editDocs, setEditDocs] = useState([]);
  const [editVideos, setEditVideos] = useState([]);
  
  // Controle de arquivos a remover
  const [deletedFotos, setDeletedFotos] = useState([]);
  const [deletedDocs, setDeletedDocs] = useState([]);
  const [deletedVideos, setDeletedVideos] = useState([]);
  const [fotoAmpliadaUrl, setFotoAmpliadaUrl] = useState(null);
  const [fotoAmpliadaSrc, setFotoAmpliadaSrc] = useState('');
  const [fotoConfirmarExclusao, setFotoConfirmarExclusao] = useState(null);

  // Plantas disponíveis para edição de chamado
  const [editPlantasList, setEditPlantasList] = useState([]);

  // Modal de fechamento com relatório
  const [fechamentoModalOpen, setFechamentoModalOpen] = useState(false);
  const [relatorioFechamentoDraft, setRelatorioFechamentoDraft] = useState(getEmptyClosingReport());
  const [fechamentoContext, setFechamentoContext] = useState({ mode: 'quick', chamadoId: null, chamado: null });
  const [fechamentoMissingFields, setFechamentoMissingFields] = useState([]);
  const [fechamentoSubmitting, setFechamentoSubmitting] = useState(false);

  // Modal de pausa
  const [pausaModalOpen, setPausaModalOpen] = useState(false);
  const [pausaMotivo, setPausaMotivo] = useState('');
  const [chamadoPausaId, setChamadoPausaId] = useState(null);

  // Filtro ativo por métrica
  const [filtroMetrica, setFiltroMetrica] = useState(null);

  // Tab principal (Buscar vs Ocorrências)
  const [mainTabIndex, setMainTabIndex] = useState(0);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  // Popup de emergência pendente
  const [emergenciaPopupOpen, setEmergenciaPopupOpen] = useState(false);
  const [emergenciaPendente, setEmergenciaPendente] = useState(null);
  const [emergenciaPopupMuted, setEmergenciaPopupMuted] = useState(false);
  const emergenciaMuteTimeoutRef = useRef(null);
  const emergenciaPopupStateRef = useRef({
    muted: false,
    editModalOpen: false,
    fechamentoModalOpen: false,
    pausaModalOpen: false,
  });

  const updateEmergenciaPopupMuted = (muted) => {
    emergenciaPopupStateRef.current = {
      ...emergenciaPopupStateRef.current,
      muted,
    };
    setEmergenciaPopupMuted(muted);
  };

  // Planta Baixa - Multi-plantas
  const [plantaDialogOpen, setPlantaDialogOpen] = useState(false);
  const [plantaDialogData, setPlantaDialogData] = useState({ imagem: null, chamados: [], chamadoId: null, plantas: [] });
  const [plantaDialogLoading, setPlantaDialogLoading] = useState(false);
  const [plantaEditPosition, setPlantaEditPosition] = useState(null);
  const [plantaDialogSelectedId, setPlantaDialogSelectedId] = useState(null);

  // Verificar nível de acesso do usuário
  const userRole = (user?.nivel_acesso || '').toLowerCase();
  const isManutencao = userRole === 'manutenção' || userRole === 'manutencao';
  const isSuporte = userRole === 'suporte';
  const isAdmin = userRole === 'administrador' || userRole === 'admin' || userRole === 'willians';
  const canDeleteFotos = isAdmin || isManutencao;
  const isEditandoEmergencia = (chamadoEditando?.tipo || '').toLowerCase() === 'emergencia';
  const canEditChamadoFields = !isManutencao || isEditandoEmergencia;
  const usuariosById = useMemo(
    () => new Map(usuarios.map((usuario) => [String(usuario.id), usuario])),
    [usuarios]
  );

  const API = '/api';

  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  useEffect(() => {
    emergenciaPopupStateRef.current = {
      muted: emergenciaPopupMuted,
      editModalOpen,
      fechamentoModalOpen,
      pausaModalOpen,
    };
  }, [emergenciaPopupMuted, editModalOpen, fechamentoModalOpen, pausaModalOpen]);

  const commentReadStorageKey = useMemo(() => {
    const userKey = user?.id || user?.username || user?.email || 'anon';
    return `chamados-comments-read:${userKey}`;
  }, [user?.email, user?.id, user?.username]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const raw = window.localStorage.getItem(commentReadStorageKey);
      const parsed = raw ? JSON.parse(raw) : {};
      setComentariosLidosPorChamado(parsed && typeof parsed === 'object' ? parsed : {});
    } catch {
      setComentariosLidosPorChamado({});
    }
  }, [commentReadStorageKey]);

  const persistComentariosLidos = (nextState) => {
    setComentariosLidosPorChamado(nextState);

    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(commentReadStorageKey, JSON.stringify(nextState));
    } catch {
      // Ignora falhas de storage no navegador.
    }
  };

  const getComentarioTimestampValue = (comentario) => {
    const timestamp = new Date(comentario?.criado_em || 0).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  const markComentariosComoLidos = (chamado) => {
    if (!chamado?.id) return;

    const comentarios = Array.isArray(chamado.comentarios_internos) ? chamado.comentarios_internos : [];
    const latestTimestamp = comentarios.reduce(
      (maxValue, comentario) => Math.max(maxValue, getComentarioTimestampValue(comentario)),
      0
    );

    if (!latestTimestamp) return;

    const nextState = {
      ...comentariosLidosPorChamado,
      [String(chamado.id)]: latestTimestamp,
    };
    persistComentariosLidos(nextState);
  };

  const getComentariosNaoLidosCount = (chamado) => {
    const comentarios = Array.isArray(chamado?.comentarios_internos) ? chamado.comentarios_internos : [];
    if (!comentarios.length) return 0;

    const currentUserId = Number(user?.id || 0);
    const lastReadTimestamp = Number(comentariosLidosPorChamado[String(chamado.id)] || 0);

    return comentarios.filter((comentario) => {
      const timestamp = getComentarioTimestampValue(comentario);
      if (!timestamp || timestamp <= lastReadTimestamp) return false;
      if (currentUserId > 0 && Number(comentario?.autor_id || 0) === currentUserId) return false;
      return true;
    }).length;
  };

  const syncChamadoInState = (updatedChamado) => {
    if (!updatedChamado?.id) return;

    setChamados((prev) => prev.map((item) => (item.id === updatedChamado.id ? updatedChamado : item)));
    setChamadoDetalhes((prev) => (prev?.id === updatedChamado.id ? updatedChamado : prev));
    setChamadoEditando((prev) => (prev?.id === updatedChamado.id ? updatedChamado : prev));
    setChatChamado((prev) => (prev?.id === updatedChamado.id ? updatedChamado : prev));
  };

  const getWhatsappMediaCount = (chamado, kind) =>
    filterWhatsappMediaByKind(getChamadoWhatsappMediaList(chamado), kind).length;

  // IDs dos clientes permitidos pelo grupo do usuário (null = todos)
  const [allowedClienteIds, setAllowedClienteIds] = useState(null);

  // Carregar clientes respeitando permissões de grupo
  useEffect(() => {
    if (!open) return;
    const fetchClientes = async () => {
      try {
        // 1. Verificar clientes permitidos pelo grupo
        let clienteIds = null;
        try {
          const meRes = await fetch(`${API}/me/clientes`, { headers });
          if (meRes.ok) {
            const meData = await meRes.json();
            clienteIds = meData.clientes || [];
          }
        } catch (e) {
          console.warn('Endpoint /me/clientes não disponível');
        }

        // 2. Buscar todos os clientes
        const res = await fetch(`${API}/clientes`, { headers });
        let data = await res.json();
        data = Array.isArray(data) ? data : [];

        // 3. Filtrar se houver restrição de grupo (manutenção/suporte)
        if (clienteIds && clienteIds.length > 0 && !isAdmin) {
          data = data.filter((c) => clienteIds.map(Number).includes(Number(c.id)));
          setAllowedClienteIds(clienteIds.map(Number));
        } else {
          setAllowedClienteIds(null); // sem restrição
        }

        setClientes(data);
      } catch (err) {
        console.error('Erro ao buscar clientes:', err);
      }
    };
    fetchClientes();
  }, [open, headers, API, isAdmin]);

  useEffect(() => {
    if (!open || !isAdmin) return;

    const fetchUsuarios = async () => {
      try {
        const res = await fetch(`${API}/usuarios`, { headers });
        const data = await res.json();
        const items = Array.isArray(data) ? data : [];
        setUsuarios(items.sort((left, right) => (left.nome || '').localeCompare(right.nome || '', 'pt-BR')));
      } catch (err) {
        console.error('Erro ao buscar usuários:', err);
      }
    };

    fetchUsuarios();
  }, [open, isAdmin, headers, API]);

  // Carregar métricas (filtradas pelo grupo se necessário)
  useEffect(() => {
    if (!open) return;
    const fetchMetricas = async () => {
      try {
        let url = `${API}/chamados/metricas`;
        if (allowedClienteIds && allowedClienteIds.length > 0) {
          const params = new URLSearchParams();
          allowedClienteIds.forEach((id) => params.append('cliente_ids[]', id));
          url += `?${params.toString()}`;
        }
        const res = await fetch(url, { headers });
        const data = await res.json();
        setMetricas(data);
      } catch (err) {
        console.error('Erro ao buscar métricas:', err);
      }
    };
    fetchMetricas();
  }, [open, headers, API, allowedClienteIds]);

  // Buscar chamados
  const buscarChamados = async (overrideFiltros = {}) => {
    setLoading(true);
    try {
      const filtros = {
        clienteId,
        status,
        prioridade,
        tipo,
        busca,
        dataInicio,
        dataFim,
        prazoInicio,
        prazoFim,
        prazoVencido,
        ...overrideFiltros,
      };

      const params = new URLSearchParams();
      if (filtros.clienteId) params.append('cliente_id', filtros.clienteId);
      if (filtros.status) params.append('status', filtros.status);
      if (filtros.prioridade) params.append('prioridade', filtros.prioridade);
      if (filtros.tipo) params.append('tipo', filtros.tipo);
      if (filtros.busca) params.append('busca', filtros.busca);
      if (filtros.dataInicio) params.append('data_inicio', filtros.dataInicio);
      if (filtros.dataFim) params.append('data_fim', filtros.dataFim);
      if (filtros.prazoInicio) params.append('prazo_inicio', filtros.prazoInicio);
      if (filtros.prazoFim) params.append('prazo_fim', filtros.prazoFim);
      if (filtros.prazoVencido) params.append('prazo_vencido', 'true');
      // Filtrar por grupo: enviar cliente_ids permitidos
      if (allowedClienteIds && allowedClienteIds.length > 0) {
        allowedClienteIds.forEach((id) => params.append('cliente_ids[]', id));
      }

      const url = `${API}/chamados${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      setChamados(Array.isArray(data) ? data : []);
      setPage(0);
    } catch (err) {
      console.error('Erro ao buscar chamados:', err);
    } finally {
      setLoading(false);
    }
  };

  const isChamadoEmergenciaPendente = (chamado) => {
    if (!chamado) return false;
    const tipoChamado = (chamado.tipo || '').toLowerCase();
    const statusChamado = (chamado.status || '').toLowerCase();
    return tipoChamado === 'emergencia' && !['resolvido', 'fechado', 'em_andamento'].includes(statusChamado);
  };

  const getPrimeiraEmergenciaPendente = (items) => {
    if (!Array.isArray(items)) return null;

    const pendentes = items.filter(isChamadoEmergenciaPendente);
    if (pendentes.length === 0) return null;

    const toTimestamp = (chamado) => {
      const value = chamado?.data_abertura || chamado?.created_at || chamado?.updated_at || null;
      const timestamp = value ? new Date(value).getTime() : 0;
      return Number.isFinite(timestamp) ? timestamp : 0;
    };

    return [...pendentes].sort((a, b) => toTimestamp(a) - toTimestamp(b))[0] || null;
  };

  const buscarEmergenciaPendente = async () => {
    if (!open) return;

    try {
      const params = new URLSearchParams();
      params.append('tipo', 'emergencia');
      if (allowedClienteIds && allowedClienteIds.length > 0) {
        allowedClienteIds.forEach((id) => params.append('cliente_ids[]', id));
      }

      const res = await fetch(`${API}/chamados?${params.toString()}`, { headers });
      if (!res.ok) return;

      const data = await res.json();
      const primeiraEmergencia = getPrimeiraEmergenciaPendente(Array.isArray(data) ? data : []);
      setEmergenciaPendente(primeiraEmergencia);

      const {
        muted,
        editModalOpen: editModalAberto,
        fechamentoModalOpen: fechamentoModalAberto,
        pausaModalOpen: pausaModalAberto,
      } = emergenciaPopupStateRef.current;

      if (
        primeiraEmergencia &&
        !muted &&
        !editModalAberto &&
        !fechamentoModalAberto &&
        !pausaModalAberto
      ) {
        setEmergenciaPopupOpen(true);
      }
    } catch (err) {
      console.error('Erro ao buscar emergência pendente:', err);
    }
  };

  // Busca inicial - aguardar carregamento de clientes permitidos
  const [clientesCarregados, setClientesCarregados] = useState(false);

  // Marcar quando clientes foram carregados (dentro do useEffect de clientes)
  useEffect(() => {
    if (clientes.length > 0 || allowedClienteIds !== null) {
      setClientesCarregados(true);
    }
  }, [clientes, allowedClienteIds]);

  useEffect(() => {
    if (open && clientesCarregados) {
      buscarChamados();
      buscarEmergenciaPendente();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientesCarregados, allowedClienteIds]);

  useEffect(() => {
    if (!open) {
      if (emergenciaMuteTimeoutRef.current) {
        window.clearTimeout(emergenciaMuteTimeoutRef.current);
        emergenciaMuteTimeoutRef.current = null;
      }
      setEmergenciaPopupOpen(false);
      setEmergenciaPendente(null);
      updateEmergenciaPopupMuted(false);
      return;
    }

    updateEmergenciaPopupMuted(false);
    const intervalId = window.setInterval(() => {
      buscarEmergenciaPendente();
    }, 15000);

    return () => {
      window.clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allowedClienteIds]);

  // Busca automática quando o tipo muda
  useEffect(() => {
    if (open && tipo !== undefined) {
      buscarChamados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);

  useEffect(() => {
    if (!open) return;

    if (!clienteId) {
      setNumeroChamado('');
      setNumeroChamadoOptions([]);
      return;
    }

    const fetchNumerosChamados = async () => {
      try {
        const params = new URLSearchParams();
        params.append('cliente_id', clienteId);
        if (allowedClienteIds && allowedClienteIds.length > 0) {
          allowedClienteIds.forEach((id) => params.append('cliente_ids[]', id));
        }

        const res = await fetch(`${API}/chamados?${params.toString()}`, { headers });
        if (!res.ok) {
          throw new Error(`Falha ao buscar números de chamados (HTTP ${res.status})`);
        }

        const data = await res.json();
        const items = Array.isArray(data) ? data : [];
        const options = [...items]
          .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0))
          .map((item) => ({
            value: String(item.id),
            label: `#${item.id}${item.titulo ? ` - ${item.titulo}` : ''}`,
          }));

        setNumeroChamadoOptions(options);
      } catch (err) {
        console.error('Erro ao carregar números de chamados por loja:', err);
        setNumeroChamadoOptions([]);
      }
    };

    fetchNumerosChamados();
  }, [open, clienteId, headers, API, allowedClienteIds]);

  const limparFiltros = () => {
    setClienteId('');
    setNumeroChamado('');
    setNumeroChamadoOptions([]);
    setStatus('');
    setPrioridade('');
    setTipo('');
    setBusca('');
    setDataInicio('');
    setDataFim('');
    setPrazoInicio('');
    setPrazoFim('');
    setPrazoVencido(false);
    buscarChamados({
      clienteId: '', status: '', prioridade: '', tipo: '', busca: '',
      dataInicio: '', dataFim: '', prazoInicio: '', prazoFim: '', prazoVencido: false,
    });
  };

  const handleFecharPopupEmergencia = () => {
    setEmergenciaPopupOpen(false);
    updateEmergenciaPopupMuted(true);

    if (emergenciaMuteTimeoutRef.current) {
      window.clearTimeout(emergenciaMuteTimeoutRef.current);
    }

    emergenciaMuteTimeoutRef.current = window.setTimeout(() => {
      updateEmergenciaPopupMuted(false);
      emergenciaMuteTimeoutRef.current = null;
    }, EMERGENCIA_LEMBRAR_DEPOIS_MS);
  };

  const handleTratarEmergenciaAgora = () => {
    if (!emergenciaPendente) {
      setEmergenciaPopupOpen(false);
      return;
    }

    if (emergenciaMuteTimeoutRef.current) {
      window.clearTimeout(emergenciaMuteTimeoutRef.current);
      emergenciaMuteTimeoutRef.current = null;
    }

    setEmergenciaPopupOpen(false);
    updateEmergenciaPopupMuted(true);
    setExpandedRow(emergenciaPendente.id);
    handleEdit(emergenciaPendente);
  };

  // Status original do chamado ao abrir edição (para detectar reabertura)
  const [statusOriginalEdicao, setStatusOriginalEdicao] = useState('');

  const getResponsavelPadrao = () => user?.nome || user?.username || 'Usuário';

  const handleEditStatusChange = (nextStatus) => {
    if (!chamadoEditando) return;

    const updatedChamado = {
      ...chamadoEditando,
      status: nextStatus,
      atendente: nextStatus === 'fechado' ? chamadoEditando.atendente || getResponsavelPadrao() : chamadoEditando.atendente,
    };

    setChamadoEditando(updatedChamado);

    if (nextStatus === 'fechado' && statusOriginalEdicao !== 'fechado') {
      handleOpenFechamentoModal(updatedChamado, 'edit');
    }
  };

  const handleEdit = async (chamado) => {
    setStatusOriginalEdicao(chamado.status || '');
    setChamadoEditando({ ...chamado, observacao_reabertura: '', motivo_pausa: '' });
    setEditFotos([]);
    setEditDocs([]);
    setEditVideos([]);
    setDeletedFotos([]);
    setDeletedDocs([]);
    setDeletedVideos([]);
    setFotoAmpliadaUrl(null);
    setFotoAmpliadaSrc('');
    setFotoConfirmarExclusao(null);
    setEditPlantasList([]);
    setEditModalOpen(true);

    if (isManutencao) {
      const chamadoEmergencia = (chamado.tipo || '').toLowerCase() === 'emergencia';
      setSnack({
        open: true,
        message: chamadoEmergencia
          ? 'Modo manutenção: emergência liberada para edição e alteração de status.'
          : 'Modo manutenção: você pode adicionar novas mídias a este chamado.',
        severity: 'info'
      });
    }

    // Carregar plantas ativas do cliente
    if (chamado.cliente_id) {
      try {
        const res = await fetch(`${API}/clientes/${chamado.cliente_id}/planta_chamados`, { headers });
        if (res.ok) {
          const data = await res.json();
          setEditPlantasList((data.plantas || []).filter((p) => !p.arquivada));
        }
      } catch (err) {
        console.error('Erro ao carregar plantas para edição:', err);
      }
    }
  };

  const abrirFotoAmpliada = async (url) => {
    if (!chamadoEditando?.id || !url) return;

    setFotoAmpliadaUrl(url);
    setFotoAmpliadaSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });

    try {
      const res = await fetch(`${API}/chamados/${chamadoEditando.id}/anexo?path=${encodeURIComponent(url)}`, { headers });
      if (!res.ok) throw new Error('Falha ao carregar foto');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      setFotoAmpliadaSrc(objectUrl);
    } catch (err) {
      console.error('Erro ao ampliar foto:', err);
      setSnack({ open: true, message: 'Erro ao carregar foto para ampliar', severity: 'error' });
    }
  };

  const fecharFotoAmpliada = () => {
    setFotoAmpliadaUrl(null);
    setFotoAmpliadaSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  };

  const confirmarApagarFoto = () => {
    if (!fotoConfirmarExclusao) return;
    setDeletedFotos((prev) => (prev.includes(fotoConfirmarExclusao) ? prev : [...prev, fotoConfirmarExclusao]));
    if (fotoAmpliadaUrl === fotoConfirmarExclusao) fecharFotoAmpliada();
    setFotoConfirmarExclusao(null);
  };

  // Handlers para arquivos na edição
  const handleEditFotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    const newItems = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setEditFotos((prev) => [...prev, ...newItems]);
  };
  const handleEditDocChange = (e) => {
    const files = Array.from(e.target.files || []);
    const newItems = files.map((file) => ({
      file,
      name: file.name
    }));
    setEditDocs((prev) => [...prev, ...newItems]);
  };
  const handleEditVideoChange = (e) => {
    const files = Array.from(e.target.files || []);
    const newItems = files.map((file) => ({
      file,
      name: file.name
    }));
    setEditVideos((prev) => [...prev, ...newItems]);
  };
  const removeEditFoto = (index) => setEditFotos((prev) => prev.filter((_, i) => i !== index));
  const removeEditDoc = (index) => setEditDocs((prev) => prev.filter((_, i) => i !== index));
  const removeEditVideo = (index) => setEditVideos((prev) => prev.filter((_, i) => i !== index));

  // Manutenção: botão "Ok" que muda status para em_andamento
  const handleOkManutencao = async (id) => {
    try {
      const res = await fetch(`${API}/chamados/${id}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'em_andamento',
          atendente: user?.username || user?.nome || 'Manutenção',
        }),
      });
      if (res.ok) {
        setSnack({ open: true, message: 'Chamado marcado como em andamento!', severity: 'success' });
        buscarChamados();
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ open: true, message: data.error || 'Erro ao atualizar chamado', severity: 'error' });
      }
    } catch (err) {
      console.error('Erro ao marcar chamado Ok:', err);
      setSnack({ open: true, message: 'Erro ao atualizar chamado', severity: 'error' });
    }
  };

  // Manutenção: botão "Resolvido" que muda status para resolvido
  const handleResolvidoManutencao = async (id) => {
    try {
      const res = await fetch(`${API}/chamados/${id}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'resolvido',
          atendente: user?.username || user?.nome || 'Manutenção',
        }),
      });
      if (res.ok) {
        setSnack({ open: true, message: 'Chamado marcado como resolvido!', severity: 'success' });
        buscarChamados();
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ open: true, message: data.error || 'Erro ao atualizar chamado', severity: 'error' });
      }
    } catch (err) {
      console.error('Erro ao marcar chamado como resolvido:', err);
      setSnack({ open: true, message: 'Erro ao atualizar chamado', severity: 'error' });
    }
  };

  // Salvar edição
  const handleSaveEdit = async () => {
    if (!chamadoEditando) return;

    // Validação: reabertura exige observação
    const estaReabrindo = chamadoEditando.status === 'aberto' && statusOriginalEdicao !== 'aberto';
    if (estaReabrindo && !(chamadoEditando.observacao_reabertura || '').trim()) {
      setSnack({ open: true, message: 'Ao reabrir um chamado, é obrigatório informar uma observação de reabertura.', severity: 'warning' });
      return;
    }

    // Validação: pausa exige motivo
    const estaPausando = chamadoEditando.status === 'pausado' && statusOriginalEdicao !== 'pausado';
    if (estaPausando && !(chamadoEditando.motivo_pausa || '').trim()) {
      setSnack({ open: true, message: 'Ao pausar um chamado, é obrigatório informar o motivo da pausa.', severity: 'warning' });
      return;
    }

    const estaFechando = chamadoEditando.status === 'fechado' && statusOriginalEdicao !== 'fechado';
    if (estaFechando) {
      const missingFields = getClosingReportMissingFields(chamadoEditando.relatorio_fechamento);
      if (missingFields.length > 0) {
        handleOpenFechamentoModal(chamadoEditando, 'edit');
        setSnack({
          open: true,
          message: `Preencha o relatório de fechamento: ${missingFields.join(', ')}`,
          severity: 'warning',
        });
        return;
      }
    }

    try {
      const payload = { ...chamadoEditando };
      // Só enviar observacao_reabertura se estiver reabrindo
      if (!estaReabrindo) {
        delete payload.observacao_reabertura;
      }
      // Só enviar motivo_pausa se estiver pausando
      if (!estaPausando) {
        delete payload.motivo_pausa;
      }

      if (payload.relatorio_fechamento) {
        const report = normalizeClosingReport(payload.relatorio_fechamento);
        payload.relatorio_fechamento = serializeClosingReport(report);
        payload.pendencia = report.pendencias_observacoes || '';
        payload.atendente = payload.atendente || report.tecnico_responsavel || getResponsavelPadrao();
      }

      const formData = new FormData();
      
      Object.keys(payload).forEach(key => {
        if (payload[key] !== null && payload[key] !== undefined && key !== 'fotos' && key !== 'documentos' && key !== 'videos' && key !== 'audio_url' && key !== 'cliente') {
          formData.append(key, payload[key]);
        }
      });

      editFotos.forEach((f) => {
        if (f.file) formData.append('fotos[]', f.file);
      });
      editDocs.forEach((d) => {
        if (d.file) formData.append('documentos[]', d.file);
      });
      editVideos.forEach((v) => {
        if (v.file) formData.append('videos[]', v.file);
      });
      deletedFotos.forEach((df) => formData.append('deleted_fotos[]', df));
      deletedDocs.forEach((dd) => formData.append('deleted_documentos[]', dd));
      deletedVideos.forEach((dv) => formData.append('deleted_videos[]', dv));

      const reqHeaders = { ...headers };
      delete reqHeaders['Content-Type'];

      const res = await fetch(`${API}/chamados/${chamadoEditando.id}`, {
        method: 'PUT',
        headers: reqHeaders,
        body: formData,
      });
      if (res.ok) {
        setSnack({
          open: true,
          message: isManutencao && !isEditandoEmergencia ? 'Mídias adicionadas com sucesso!' : 'Chamado atualizado com sucesso!',
          severity: 'success'
        });
        setEditModalOpen(false);
        setChamadoEditando(null);
        buscarChamados();
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ open: true, message: data.error || 'Erro ao atualizar chamado', severity: 'error' });
      }
    } catch (err) {
      console.error('Erro ao atualizar:', err);
      setSnack({ open: true, message: 'Erro ao atualizar chamado', severity: 'error' });
    }
  };

  // Filtro por métrica
  const handleFiltroMetrica = (tipo) => {
    const proximoFiltro = filtroMetrica === tipo ? null : tipo;

    let proximoStatus = '';
    if (proximoFiltro === 'abertos') proximoStatus = 'aberto';
    else if (proximoFiltro === 'em_andamento') proximoStatus = 'em_andamento';
    else if (proximoFiltro === 'resolvidos') proximoStatus = 'resolvido';
    else if (proximoFiltro === 'aguardando_resposta') proximoStatus = 'aguardando_resposta';
    else if (proximoFiltro === 'pendencias') proximoStatus = 'fechado';

    setFiltroMetrica(proximoFiltro);
    setStatus(proximoStatus);
    buscarChamados({ status: proximoStatus });
  };

  const handleView = (chamado) => {
    setChamadoDetalhes(chamado);
    setViewModalOpen(true);
  };

  const handleOpenChatModal = (chamado) => {
    setChatChamado(chamado);
    setNovoComentarioChat('');
    setChatModalOpen(true);
    markComentariosComoLidos(chamado);
  };

  const handleCloseChatModal = () => {
    if (chatSubmitting) return;
    setChatModalOpen(false);
    setChatChamado(null);
    setNovoComentarioChat('');
  };

  const handleSalvarComentarioChat = async () => {
    const texto = novoComentarioChat.trim();
    if (!chatChamado?.id) return;
    if (!texto) {
      setSnack({ open: true, message: 'Digite um comentário.', severity: 'warning' });
      return;
    }

    setChatSubmitting(true);
    try {
      const res = await fetch(`${API}/chamados/${chatChamado.id}/comentarios`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ texto }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSnack({ open: true, message: data.error || 'Erro ao salvar comentário.', severity: 'error' });
        return;
      }

      const updatedChamado = data.chamado || data;
      syncChamadoInState(updatedChamado);
      setChatChamado(updatedChamado);
      markComentariosComoLidos(updatedChamado);
      setNovoComentarioChat('');
      setSnack({ open: true, message: 'Comentário registrado.', severity: 'success' });
    } catch (err) {
      console.error('Erro ao salvar comentário do chamado:', err);
      setSnack({ open: true, message: 'Erro ao salvar comentário.', severity: 'error' });
    } finally {
      setChatSubmitting(false);
    }
  };

  // Abrir planta do cliente com chamados marcados
  const handleOpenPlantaDialog = async (chamado) => {
    setPlantaDialogOpen(true);
    setPlantaDialogLoading(true);
    setPlantaEditPosition(chamado.planta_x != null ? { x: chamado.planta_x, y: chamado.planta_y } : null);
    try {
      const res = await fetch(`${API}/clientes/${chamado.cliente_id}/planta_chamados`, { headers });
      if (res.ok) {
        const data = await res.json();
        const plantasAtivas = (data.plantas || []).filter((p) => !p.arquivada);
        const plantasArquivadas = data.plantas_arquivadas || [];
        const todasPlantas = [...plantasAtivas, ...plantasArquivadas];
        // Select the planta associated with this chamado, or the first active one
        const targetPlantaId = chamado.planta_id || (plantasAtivas.length > 0 ? plantasAtivas[0].id : null);
        // Check if the chamado's planta is archived
        const plantaArquivada = chamado.planta_id ? plantasArquivadas.find((p) => p.id === chamado.planta_id) : null;
        const targetPlanta = todasPlantas.find((p) => p.id === targetPlantaId) || plantasAtivas[0];
        setPlantaDialogData({
          imagem: targetPlanta?.planta_imagem || data.planta_imagem,
          chamados: targetPlanta?.chamados || data.chamados || [],
          chamadoId: chamado.id,
          chamadoClienteId: chamado.cliente_id,
          plantas: plantasAtivas,
          plantaArquivada: plantaArquivada || null,
        });
        setPlantaDialogSelectedId(plantaArquivada ? null : targetPlantaId);
      }
    } catch (e) {
      console.error('Erro ao carregar planta:', e);
    } finally {
      setPlantaDialogLoading(false);
    }
  };

  // Atualizar planta do chamado (de arquivada para ativa)
  const handleAtualizarPlantaChamado = async (novaPlantaId) => {
    if (!plantaDialogData.chamadoId || !novaPlantaId) return;
    try {
      const res = await fetch(`${API}/chamados/${plantaDialogData.chamadoId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ planta_id: novaPlantaId }),
      });
      if (res.ok) {
        setSnack({ open: true, message: 'Planta do chamado atualizada!', severity: 'success' });
        // Reload planta dialog with updated data
        const chamadoAtualizado = await res.json();
        handleOpenPlantaDialog(chamadoAtualizado);
        buscarChamados();
      }
    } catch (e) {
      setSnack({ open: true, message: 'Erro ao atualizar planta', severity: 'error' });
    }
  };

  // Salvar posição na planta
  const handleSavePlantaPosition = async () => {
    if (!plantaDialogData.chamadoId || !plantaEditPosition) return;
    try {
      const body = { planta_x: plantaEditPosition.x, planta_y: plantaEditPosition.y };
      if (plantaDialogSelectedId) body.planta_id = plantaDialogSelectedId;
      const res = await fetch(`${API}/chamados/${plantaDialogData.chamadoId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setSnack({ open: true, message: 'Posição na planta atualizada!', severity: 'success' });
        setPlantaDialogOpen(false);
        buscarChamados();
      }
    } catch (e) {
      setSnack({ open: true, message: 'Erro ao salvar posição', severity: 'error' });
    }
  };

  // Iniciar atendimento
  const handleIniciarAtendimento = async (id) => {
    try {
      const res = await fetch(`${API}/chamados/${id}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'em_andamento',
          atendente: user?.username || user?.nome || 'Usuário',
        }),
      });
      if (res.ok) {
        setSnack({ open: true, message: 'Atendimento iniciado!', severity: 'success' });
        buscarChamados();
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ open: true, message: data.error || 'Erro ao iniciar atendimento', severity: 'error' });
      }
    } catch (err) {
      console.error('Erro ao iniciar atendimento:', err);
      setSnack({ open: true, message: 'Erro ao iniciar atendimento', severity: 'error' });
    }
  };

  // Finalizar como tratado
  const handleTratado = async (id) => {
    try {
      const res = await fetch(`${API}/chamados/${id}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'resolvido',
          atendente: user?.username || user?.nome || 'Usuário',
        }),
      });
      if (res.ok) {
        setSnack({ open: true, message: 'Chamado tratado com sucesso!', severity: 'success' });
        buscarChamados();
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ open: true, message: data.error || 'Erro ao finalizar chamado', severity: 'error' });
      }
    } catch (err) {
      console.error('Erro ao finalizar chamado:', err);
      setSnack({ open: true, message: 'Erro ao finalizar chamado', severity: 'error' });
    }
  };

  const handleCloseFechamentoModal = () => {
    setFechamentoModalOpen(false);
    setRelatorioFechamentoDraft(getEmptyClosingReport());
    setFechamentoContext({ mode: 'quick', chamadoId: null, chamado: null });
    setFechamentoMissingFields([]);
    setFechamentoSubmitting(false);
  };

  const handleOpenFechamentoModal = (chamado, mode = 'quick') => {
    if (!chamado) return;

    const chamadoBase = {
      ...chamado,
      cliente_nome: chamado.cliente_nome || getClienteNome(chamado.cliente_id),
    };
    const draft = buildClosingReportDraft(chamadoBase, user);

    if (!draft.tecnico_responsavel) {
      draft.tecnico_responsavel = getResponsavelPadrao();
    }

    setFechamentoContext({ mode, chamadoId: chamadoBase.id, chamado: chamadoBase });
    setRelatorioFechamentoDraft(draft);
    setFechamentoModalOpen(true);
  };

  const handleConfirmarFechamento = async () => {
    const report = normalizeClosingReport(relatorioFechamentoDraft);
    const missingFields = getClosingReportMissingFields(report);

    if (missingFields.length > 0) {
      setFechamentoMissingFields(missingFields);
      setSnack({
        open: true,
        message: `Preencha os campos obrigatórios antes de aplicar: ${missingFields.join(', ')}`,
        severity: 'warning',
      });
      return;
    }

    setFechamentoMissingFields([]);

    const payload = {
      status: 'fechado',
      atendente: report.tecnico_responsavel || getResponsavelPadrao(),
      pendencia: report.pendencias_observacoes || '',
      relatorio_fechamento: serializeClosingReport(report),
    };

    const chamadoId = fechamentoContext.chamadoId;
    if (!chamadoId) {
      setSnack({ open: true, message: 'Chamado inválido para fechamento.', severity: 'error' });
      return;
    }

    setFechamentoSubmitting(true);
    try {
      const res = await fetch(`${API}/chamados/${chamadoId}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        if (fechamentoContext.mode === 'edit') {
          setChamadoEditando((prev) =>
            prev
              ? {
                  ...prev,
                  status: 'fechado',
                  atendente: payload.atendente,
                  pendencia: payload.pendencia,
                  relatorio_fechamento: payload.relatorio_fechamento,
                }
              : prev
          );
        }
        setSnack({ open: true, message: 'Chamado fechado com relatório!', severity: 'success' });
        handleCloseFechamentoModal();
        buscarChamados();
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ open: true, message: data.error || 'Erro ao fechar chamado', severity: 'error' });
      }
    } catch (err) {
      console.error('Erro ao fechar chamado:', err);
      setSnack({ open: true, message: 'Erro ao fechar chamado', severity: 'error' });
    } finally {
      setFechamentoSubmitting(false);
    }
  };

  // Abrir modal de pausa
  const handleOpenPausaModal = (id) => {
    setChamadoPausaId(id);
    setPausaMotivo('');
    setPausaModalOpen(true);
  };

  // Confirmar pausa com motivo
  const handleConfirmarPausa = async () => {
    if (!pausaMotivo.trim()) {
      setSnack({ open: true, message: 'Descreva o motivo da pausa', severity: 'warning' });
      return;
    }
    try {
      const res = await fetch(`${API}/chamados/${chamadoPausaId}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'pausado',
          atendente: user?.username || user?.nome || 'Usuário',
          motivo_pausa: pausaMotivo,
        }),
      });
      if (res.ok) {
        setSnack({ open: true, message: 'Chamado pausado', severity: 'info' });
        setPausaModalOpen(false);
        buscarChamados();
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ open: true, message: data.error || 'Erro ao pausar chamado', severity: 'error' });
      }
    } catch (err) {
      console.error('Erro ao pausar chamado:', err);
      setSnack({ open: true, message: 'Erro ao pausar chamado', severity: 'error' });
    }
  };

  // Retomar chamado pausado (volta para em_andamento)
  const handleRetomarChamado = async (id) => {
    try {
      const res = await fetch(`${API}/chamados/${id}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'em_andamento',
          atendente: user?.username || user?.nome || 'Usuário',
        }),
      });
      if (res.ok) {
        setSnack({ open: true, message: 'Chamado retomado!', severity: 'success' });
        buscarChamados();
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ open: true, message: data.error || 'Erro ao retomar chamado', severity: 'error' });
      }
    } catch (err) {
      console.error('Erro ao retomar chamado:', err);
      setSnack({ open: true, message: 'Erro ao retomar chamado', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este chamado?')) return;
    try {
      const res = await fetch(`${API}/chamados/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) {
        setChamados((prev) => prev.filter((c) => c.id !== id));
      }
    } catch (err) {
      console.error('Erro ao excluir:', err);
    }
  };

  const getStatusChip = (val, chamadoObj) => {
    const opt = STATUS_OPTIONS.find((o) => o.value === val);
    if (!opt) return <Chip label={val || 'N/A'} size="small" />;

    const pausas = chamadoObj?.pausas || [];
    const pausaTooltip = val === 'pausado' && pausas.length > 0
      ? pausas.map((p, i) => `Pausa ${i + 1}: ${p.motivo} (${p.pausado_em ? new Date(p.pausado_em).toLocaleString('pt-BR') : '-'})`).join('\n')
      : '';

    const chip = (
      <Chip
        label={opt.label}
        color={opt.color || 'default'}
        size="small"
        sx={
          val === 'fechado'
            ? { bgcolor: 'error.main', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }
            : val === 'pausado'
              ? { bgcolor: '#9e9e9e', color: '#fff' }
              : undefined
        }
      />
    );

    return pausaTooltip ? (
      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{pausaTooltip}</span>} arrow>{chip}</Tooltip>
    ) : chip;
  };

  const getPrioridadeChip = (val) => {
    const opt = PRIORIDADE_OPTIONS.find((o) => o.value === val);
    return opt ? (
      <Chip label={opt.label} color={opt.color || 'default'} size="small" variant="outlined" />
    ) : (
      <Chip label={val || 'N/A'} size="small" variant="outlined" />
    );
  };

  const getTipoLabel = (val) => {
    const opt = TIPO_OPTIONS.find((o) => o.value === val);
    return opt?.label || val || '-';
  };

  const getStatusLabel = (val) => {
    const opt = STATUS_OPTIONS.find((o) => o.value === val);
    return opt?.label || val || '-';
  };

  const getPrioridadeLabel = (val) => {
    const opt = PRIORIDADE_OPTIONS.find((o) => o.value === val);
    return opt?.label || val || '-';
  };

  const formatClienteFiltroLabel = (cliente) => {
    if (!cliente) return '';
    if (cliente.id === '') return 'Todos';

    const nome = (cliente.nome || '').toString().trim();
    const match = nome.match(/^(.*)\s+LOJA\s+(\d+)$/i);
    if (match) {
      const nomeLoja = match[1].trim().toUpperCase();
      const numero = match[2].padStart(2, '0');
      return `LJ ${numero} - ${nomeLoja}`;
    }

    return nome.toUpperCase();
  };

  const clienteFiltroOptions = useMemo(
    () => [{ id: '', nome: 'Todos' }, ...clientes],
    [clientes]
  );

  const clienteFiltroValue = useMemo(
    () =>
      clienteFiltroOptions.find((c) => String(c.id) === String(clienteId)) ||
      clienteFiltroOptions[0],
    [clienteFiltroOptions, clienteId]
  );

  const getClienteNome = (id) => {
    const c = clientes.find((cl) => cl.id === id);
    return c?.nome || '-';
  };

  const sanitizeFilenamePart = (value) => {
    if (!value) return 'sem-valor';
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9-_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  };

  const formatDateForFilename = (dateStr) => {
    const date = dateStr ? new Date(dateStr) : null;
    if (!date || Number.isNaN(date.getTime())) return 'sem-data';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatTimeForFilename = (dateStr) => {
    const date = dateStr ? new Date(dateStr) : null;
    if (!date || Number.isNaN(date.getTime())) return 'sem-horario';
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${hh}-${min}`;
  };

  const handleDownloadChamadoPdf = (chamado) => {
    try {
      const clienteNome = sanitizeFilenamePart(getClienteNome(chamado.cliente_id));
      const dataParte = formatDateForFilename(chamado.data_abertura || chamado.created_at);
      const horaParte = formatTimeForFilename(chamado.created_at || chamado.data_abertura);
      const numeroChamado = sanitizeFilenamePart(chamado.numero_chamado || chamado.id);
      const filename = `${clienteNome}_${dataParte}_${horaParte}_${numeroChamado}.pdf`;

      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      let y = 15;
      const lineHeight = 7;

      const writeLine = (label, value = '') => {
        pdf.text(`${label}${value}`, 15, y);
        y += lineHeight;
      };

      const writeParagraph = (label, value = '') => {
        if (!value) return;
        const lines = pdf.splitTextToSize(`${label}${value}`, 180);
        if (y + lines.length * lineHeight > 280) {
          pdf.addPage();
          y = 15;
        }
        pdf.text(lines, 15, y);
        y += lines.length * lineHeight;
      };

      pdf.setFontSize(14);
      pdf.text('Consulta de Chamado', 15, y);
      y += 10;

      pdf.setFontSize(11);
      writeLine('ID do chamado: ', String(chamado.id || '-'));
      writeLine('Cliente: ', String(getClienteNome(chamado.cliente_id) || '-'));
      writeLine('Titulo: ', String(chamado.titulo || '-'));
      writeLine('Status: ', STATUS_OPTIONS.find((s) => s.value === chamado.status)?.label || chamado.status || 'N/A');
      writeLine('Prioridade: ', PRIORIDADE_OPTIONS.find((p) => p.value === chamado.prioridade)?.label || chamado.prioridade || 'N/A');
      writeLine('Tipo: ', getTipoLabel(chamado.tipo));
      writeLine('Data de abertura: ', formatDateTime(chamado.data_abertura));
      if (chamado.prazo_conclusao) {
        writeLine('Prazo para conclusão: ', new Date(chamado.prazo_conclusao + 'T00:00:00').toLocaleDateString('pt-BR'));
      }
      if (chamado.data_fechamento) {
        writeLine('Data de fechamento: ', formatDateTime(chamado.data_fechamento));
      }
      const tempoInfo = calcTempoChamado(chamado);
      if (tempoInfo) {
        writeLine('', tempoInfo);
      }

      const descricao = String(chamado.descricao || '-');
      const descricaoLines = pdf.splitTextToSize(`Descricao: ${descricao}`, 180);
      pdf.text(descricaoLines, 15, y);
      y += descricaoLines.length * lineHeight;

      const closingReport = normalizeClosingReport(chamado.relatorio_fechamento);
      if (hasClosingReportContent(closingReport)) {
        y += 4;
        if (y > 260) {
          pdf.addPage();
          y = 15;
        }
        pdf.setFontSize(12);
        pdf.text('Relatorio de Fechamento', 15, y);
        y += lineHeight;
        pdf.setFontSize(10);

        writeParagraph('Responsavel tecnico: ', closingReport.tecnico_responsavel);
        writeParagraph('Acompanhou o servico: ', closingReport.acompanhou_servico);
        writeParagraph('Local do atendimento: ', closingReport.local_atendimento);
        writeParagraph('Problema reportado: ', closingReport.problema_reportado);
        writeParagraph('Diagnostico realizado: ', closingReport.diagnostico);
        writeParagraph('Servico executado: ', closingReport.servico_executado);
        writeParagraph('Materiais utilizados: ', closingReport.materiais_utilizados);
        writeParagraph('Orientacoes ao cliente: ', closingReport.orientacoes_cliente);
        writeParagraph('Pendencias e observacoes finais: ', closingReport.pendencias_observacoes);
        writeParagraph('Conclusao: ', closingReport.conclusao);
        writeParagraph('Assinante: ', closingReport.nome_assinante);
        writeParagraph('Cargo/Função: ', closingReport.cargo_assinante);
        writeParagraph(
          'Checklist: ',
          [
            closingReport.equipamento_liberado ? 'equipamento liberado' : 'equipamento não liberado',
            closingReport.retorno_necessario ? 'retorno necessário' : 'sem retorno previsto',
            closingReport.cliente_ciente ? 'cliente ciente' : 'cliente não validado',
          ].join(' | ')
        );

        if (closingReport.assinatura) {
          if (y > 235) {
            pdf.addPage();
            y = 15;
          }
          pdf.text('Assinatura:', 15, y);
          y += 4;
          pdf.addImage(closingReport.assinatura, 'PNG', 15, y, 65, 25);
          y += 30;
        }

        pdf.setFontSize(11);
      }

      // Histórico de pausas no PDF
      if (chamado.pausas && chamado.pausas.length > 0) {
        y += 3;
        pdf.setFontSize(12);
        pdf.text(`Historico de Pausas (${chamado.pausas.length}):`, 15, y);
        y += lineHeight;
        pdf.setFontSize(10);
        chamado.pausas.forEach((p, i) => {
          if (y > 270) { pdf.addPage(); y = 15; }
          const pausadoEm = p.pausado_em ? new Date(p.pausado_em).toLocaleString('pt-BR') : '-';
          const retomadoEm = p.retomado_em ? new Date(p.retomado_em).toLocaleString('pt-BR') : 'Ainda pausado';
          writeLine(`  Pausa ${i + 1}: `, `${p.motivo} (por ${p.pausado_por || 'Usuario'})`);
          writeLine('    ', `Pausado: ${pausadoEm} | Retomado: ${retomadoEm}`);
        });
        pdf.setFontSize(11);
      }

      pdf.save(filename);
    } catch (err) {
      console.error('Erro ao gerar PDF do chamado:', err);
      setSnack({ open: true, message: 'Erro ao gerar PDF', severity: 'error' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
    } catch {
      return dateStr;
    }
  };

  const formatCommentDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch {
      return dateStr;
    }
  };

  const calcTempoChamado = (chamado) => {
    if (!chamado?.data_abertura) return null;
    const abertura = new Date(chamado.data_abertura);
    const isFechado = ['resolvido', 'fechado'].includes(chamado.status);
    const fim = isFechado && chamado.data_fechamento ? new Date(chamado.data_fechamento) : new Date();
    const diffMs = fim - abertura;
    if (diffMs < 0) return null;
    const totalMin = Math.floor(diffMs / 60000);
    const dias = Math.floor(totalMin / 1440);
    const horas = Math.floor((totalMin % 1440) / 60);
    const minutos = totalMin % 60;
    const partes = [];
    if (dias > 0) partes.push(`${dias} dia${dias > 1 ? 's' : ''}`);
    if (horas > 0) partes.push(`${horas} hora${horas > 1 ? 's' : ''}`);
    if (partes.length === 0) partes.push(`${minutos} minuto${minutos !== 1 ? 's' : ''}`);
    if (isFechado) {
      const quem = chamado.fechado_por_nome ? ` por ${chamado.fechado_por_nome}` : '';
      return `Chamado tratado em ${partes.join(' e ')}${quem}`;
    }
    return `Chamado aberto há ${partes.join(' e ')}`;
  };

  const formatCoord = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(4) : '';
  };

  const hasValidCoords = (lat, lng) => {
    const latNum = Number(lat);
    const lngNum = Number(lng);
    return Number.isFinite(latNum) && Number.isFinite(lngNum);
  };

  const descricaoOptions = useMemo(() => {
    const source = clienteId
      ? chamados.filter((c) => String(c.cliente_id) === String(clienteId))
      : chamados;
    const titles = [...new Set(source.map((c) => c.titulo).filter(Boolean))];
    return titles.sort((a, b) => a.localeCompare(b));
  }, [chamados, clienteId]);

  const chamadosFiltrados = useMemo(() => {
    if (!numeroChamado) return chamados;
    return chamados.filter((chamado) => String(chamado.id) === String(numeroChamado));
  }, [chamados, numeroChamado]);

  // Dados paginados
  const paginatedChamados = chamadosFiltrados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SupportAgent color="primary" />
          <Typography variant="h6">Consulta de Chamados</Typography>
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Tabs principais - Buscar e Ocorrências (admin only) */}
        {isAdmin && (
          <Tabs
            value={mainTabIndex}
            onChange={(e, v) => setMainTabIndex(v)}
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Buscar Chamados" icon={<Search />} iconPosition="start" />
            <Tab label="Ocorrências" icon={<History />} iconPosition="start" />
          </Tabs>
        )}

        {/* Tab 0: Busca normal */}
        {mainTabIndex === 0 && (
          <>
            {/* Métricas - Clicáveis como filtros */}
            {metricas && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={2}>
                  <Paper 
                sx={{ 
                  p: 1.5, 
                  textAlign: 'center', 
                  bgcolor: 'info.light', 
                  color: 'info.contrastText',
                  cursor: 'pointer',
                  border: filtroMetrica === 'abertos' ? '3px solid' : 'none',
                  borderColor: 'info.dark',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.02)', boxShadow: 3 }
                }}
                onClick={() => handleFiltroMetrica('abertos')}
              >
                <Typography variant="h5">{metricas.abertos || 0}</Typography>
                <Typography variant="caption">Abertos</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Paper 
                sx={{ 
                  p: 1.5, 
                  textAlign: 'center', 
                  bgcolor: 'warning.light', 
                  color: 'warning.contrastText',
                  cursor: 'pointer',
                  border: filtroMetrica === 'em_andamento' ? '3px solid' : 'none',
                  borderColor: 'warning.dark',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.02)', boxShadow: 3 }
                }}
                onClick={() => handleFiltroMetrica('em_andamento')}
              >
                <Typography variant="h5">{metricas.em_andamento || 0}</Typography>
                <Typography variant="caption">Em Andamento</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Paper 
                sx={{ 
                  p: 1.5, 
                  textAlign: 'center', 
                  bgcolor: 'secondary.light', 
                  color: 'secondary.contrastText',
                  cursor: 'pointer',
                  border: filtroMetrica === 'aguardando_resposta' ? '3px solid' : 'none',
                  borderColor: 'secondary.dark',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.02)', boxShadow: 3 }
                }}
                onClick={() => handleFiltroMetrica('aguardando_resposta')}
              >
                <Typography variant="h5">{metricas.aguardando_resposta || 0}</Typography>
                <Typography variant="caption">Ag. Resposta</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Paper 
                sx={{ 
                  p: 1.5, 
                  textAlign: 'center', 
                  bgcolor: 'success.light', 
                  color: 'success.contrastText',
                  cursor: 'pointer',
                  border: filtroMetrica === 'resolvidos' ? '3px solid' : 'none',
                  borderColor: 'success.dark',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.02)', boxShadow: 3 }
                }}
                onClick={() => handleFiltroMetrica('resolvidos')}
              >
                <Typography variant="h5">{metricas.resolvidos || 0}</Typography>
                <Typography variant="caption">Resolvidos</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Paper 
                sx={{ 
                  p: 1.5, 
                  textAlign: 'center', 
                  bgcolor: 'error.light',
                  color: 'error.contrastText',
                  cursor: 'pointer',
                  border: filtroMetrica === 'pendencias' ? '3px solid' : 'none',
                  borderColor: 'error.dark',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.02)', boxShadow: 3 }
                }}
                onClick={() => handleFiltroMetrica('pendencias')}
              >
                <Typography variant="h5">{metricas.pendencias || 0}</Typography>
                <Typography variant="caption">Fechado</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Paper 
                sx={{ 
                  p: 1.5, 
                  textAlign: 'center', 
                  bgcolor: 'grey.300',
                  cursor: 'pointer',
                  border: filtroMetrica === 'total' ? '3px solid' : 'none',
                  borderColor: 'grey.600',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.02)', boxShadow: 3 }
                }}
                onClick={() => handleFiltroMetrica('total')}
              >
                <Typography variant="h5">{metricas.total || 0}</Typography>
                <Typography variant="caption">Total</Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 2, display: 'block' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showFilters ? 2 : 0 }}>
            <Button
              startIcon={showFilters ? <ExpandLess /> : <FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              size="small"
            >
              {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            </Button>
            {showFilters && (
              <Button startIcon={<Clear />} onClick={limparFiltros} size="small">
                Limpar
              </Button>
            )}
          </Box>

          <Collapse in={showFilters}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 280px', minWidth: { xs: '100%', sm: 260 } }}>
                <Autocomplete
                  options={clienteFiltroOptions}
                  value={clienteFiltroValue}
                  onChange={(_, option) => {
                    setClienteId(option?.id ?? '');
                    setNumeroChamado('');
                  }}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  getOptionLabel={(option) => formatClienteFiltroLabel(option)}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ fontSize: 14, fontWeight: option.id === '' ? 600 : 500 }}>
                        {formatClienteFiltroLabel(option)}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Loja"
                      size="small"
                      placeholder="Pesquisar loja"
                    />
                  )}
                  fullWidth
                  ListboxProps={{
                    sx: {
                      maxHeight: 280,
                      '& .MuiAutocomplete-option': {
                        minHeight: 34,
                        py: 0.6,
                      },
                    },
                  }}
                />
              </Box>
              {clienteId && (
                <Box sx={{ flex: '1 1 240px', minWidth: { xs: '100%', sm: 220 } }}>
                  <TextField
                    select
                    label="Número do chamado"
                    value={numeroChamado}
                    onChange={(e) => setNumeroChamado(e.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {numeroChamadoOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}
              {!isMobile && (
                <>
              <Box sx={{ flex: '1 1 240px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  select
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  fullWidth
                  size="small"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ flex: '1 1 240px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  select
                  label="Prioridade"
                  value={prioridade}
                  onChange={(e) => setPrioridade(e.target.value)}
                  fullWidth
                  size="small"
                >
                  {PRIORIDADE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ flex: '1 1 320px', minWidth: { xs: '100%', sm: 300 } }}>
                <Autocomplete
                  freeSolo
                  options={descricaoOptions.filter((t) =>
                    busca ? t.toLowerCase().includes(busca.toLowerCase()) : true
                  )}
                  inputValue={busca}
                  onInputChange={(_, value) => setBusca(value ?? '')}
                  onChange={(_, value) => {
                    if (value) {
                      setBusca(value);
                      buscarChamados({ busca: value });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar (título/descrição)"
                      size="small"
                      fullWidth
                      onKeyDown={(e) => e.key === 'Enter' && buscarChamados()}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  type="date"
                  label="Data Início"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  type="date"
                  label="Data Fim"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  type="date"
                  label="Prazo Início"
                  value={prazoInicio}
                  onChange={(e) => setPrazoInicio(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  type="date"
                  label="Prazo Fim"
                  value={prazoFim}
                  onChange={(e) => setPrazoFim(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 }, display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={<Checkbox checked={prazoVencido} onChange={(e) => setPrazoVencido(e.target.checked)} size="small" />}
                  label="Só vencidos"
                />
              </Box>
                </>
              )}
              <Box sx={{ flex: '1 1 240px', minWidth: { xs: '100%', sm: 220 } }}>
                <TextField
                  select
                  label="Tipo"
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                  fullWidth
                  size="small"
                >
                  {TIPO_OPTIONS.filter((o) => !(isManutencao && o.value === 'projetos')).map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ flex: '1 1 220px', minWidth: { xs: '100%', sm: 220 } }}>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={async () => {
                    await buscarChamados();
                    setStatus('');
                    setPrioridade('');
                    setTipo('');
                    setDataInicio('');
                    setDataFim('');
                    setPrazoInicio('');
                    setPrazoFim('');
                    setPrazoVencido(false);
                  }}
                  fullWidth
                  sx={{ height: '100%' }}
                >
                  Pesquisar
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Paper>

        {/* Tabela */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : chamadosFiltrados.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Nenhum chamado encontrado</Typography>
          </Paper>
        ) : (
          <>
            <Grid container spacing={2}>
              {paginatedChamados.map((chamado) => (
                <Grid item xs={12} sm={6} md={4} key={chamado.id}>
                  <Card sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: (() => {
                      if (!chamado.prazo_conclusao || ['resolvido','fechado'].includes(chamado.status)) return undefined;
                      const prazo = new Date(chamado.prazo_conclusao + 'T23:59:59');
                      const now = new Date();
                      const diffDays = (prazo - now) / (1000 * 60 * 60 * 24);
                      if (diffDays < 0) return '#fce4ec';
                      if (diffDays <= 2) return '#fffde7';
                      return undefined;
                    })()
                  }}>
                    <CardContent sx={{ flex: 1 }}>
                      {/* Cabeçalho: título + chip status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                          Chamado #{chamado.id}
                        </Typography>
                        {getStatusChip(chamado.status, chamado)}
                      </Box>

                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        📅 {formatDate(chamado.data_abertura)}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        👤 {getClienteNome(chamado.cliente_id)}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        🗂️ {getTipoLabel(chamado.tipo)}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        📋 {chamado.titulo}
                      </Typography>

                      {/* Chips: prioridade + mídia */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {getPrioridadeChip(chamado.prioridade)}
                        {(chamado.audio_url || getWhatsappMediaCount(chamado, 'audio') > 0) && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${(chamado.audio_url ? 1 : 0) + getWhatsappMediaCount(chamado, 'audio')} áudio(s)`}
                            icon={<Mic />}
                          />
                        )}
                        {((chamado.fotos && chamado.fotos.length > 0) || getWhatsappMediaCount(chamado, 'image') > 0) && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="success"
                            label={`${(chamado.fotos?.length || 0) + getWhatsappMediaCount(chamado, 'image')} foto(s)`}
                            icon={<Photo />}
                          />
                        )}
                        {((chamado.videos && chamado.videos.length > 0) || getWhatsappMediaCount(chamado, 'video') > 0) && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="error"
                            label={`${(chamado.videos?.length || 0) + getWhatsappMediaCount(chamado, 'video')} vídeo(s)`}
                            icon={<Videocam />}
                          />
                        )}
                        {((chamado.documentos && chamado.documentos.length > 0) || getWhatsappMediaCount(chamado, 'document') > 0) && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="warning"
                            label={`${(chamado.documentos?.length || 0) + getWhatsappMediaCount(chamado, 'document')} doc(s)`}
                            icon={<Description />}
                          />
                        )}
                      </Box>

                      {/* Localização */}
                      {hasValidCoords(chamado.latitude, chamado.longitude) ? (
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                          📍 <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => window.open(`https://maps.google.com/?q=${chamado.latitude},${chamado.longitude}`, '_blank')}>{formatCoord(chamado.latitude)}, {formatCoord(chamado.longitude)}</span>
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                          📍 Localização não registrada
                        </Typography>
                      )}

                      {chamado.data_abertura && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          🕐 Aberto: {formatDateTime(chamado.data_abertura)}
                        </Typography>
                      )}
                      {chamado.prazo_conclusao && (
                        <Typography variant="caption" color={new Date(chamado.prazo_conclusao + 'T23:59:59') < new Date() && !['resolvido','fechado'].includes(chamado.status) ? 'error' : 'text.secondary'} display="block" sx={{ mt: 0.3, fontWeight: new Date(chamado.prazo_conclusao + 'T23:59:59') < new Date() && !['resolvido','fechado'].includes(chamado.status) ? 700 : 400 }}>
                          📅 Prazo: {new Date(chamado.prazo_conclusao + 'T00:00:00').toLocaleDateString('pt-BR')}
                        </Typography>
                      )}

                      {/* Ações */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        {!isManutencao && (
                          <IconButton size="small" onClick={() => handleDownloadChamadoPdf(chamado)} title="Baixar PDF" sx={{ color: '#d32f2f' }}>
                            <Download fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" color="primary" onClick={() => handleView(chamado)} title="Visualizar">
                          <Visibility fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleOpenChatModal(chamado)}
                          title={`Chat / Comentários (${Array.isArray(chamado.comentarios_internos) ? chamado.comentarios_internos.length : 0})`}
                        >
                          <Badge
                            color="error"
                            overlap="circular"
                            badgeContent={getComentariosNaoLidosCount(chamado)}
                            invisible={getComentariosNaoLidosCount(chamado) <= 0}
                          >
                            <ChatBubbleOutline fontSize="small" />
                          </Badge>
                        </IconButton>
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleEdit(chamado)}
                          title={isManutencao && (chamado.tipo || '').toLowerCase() !== 'emergencia' ? 'Adicionar mídias' : 'Editar'}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        {!isManutencao && !isSuporte && hasPermission('/chamados', 'delete') && (
                          <IconButton size="small" color="error" onClick={() => handleDelete(chamado.id)} title="Excluir">
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                        {isManutencao && chamado.status === 'aberto' && (
                          <IconButton size="small" color="success" title="Iniciar Atendimento" onClick={() => handleOkManutencao(chamado.id)}>
                            <ThumbUp fontSize="small" />
                          </IconButton>
                        )}
                        {isManutencao && chamado.status === 'em_andamento' && (
                          <IconButton size="small" color="success" title="Marcar Resolvido" onClick={() => handleResolvidoManutencao(chamado.id)}>
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        )}
                        {chamado.status === 'em_andamento' && (
                          <IconButton size="small" title="Pausar Chamado" onClick={() => handleOpenPausaModal(chamado.id)} sx={{ color: '#9e9e9e' }}>
                            <Pause fontSize="small" />
                          </IconButton>
                        )}
                        {chamado.status === 'pausado' && (
                          <IconButton size="small" color="warning" title="Retomar Chamado" onClick={() => handleRetomarChamado(chamado.id)}>
                            <PlayArrow fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" title="Ver detalhes" onClick={() => setExpandedRow(expandedRow === chamado.id ? null : chamado.id)}>
                          {expandedRow === chamado.id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      </Box>
                    </CardContent>

                    {/* Detalhe expandido */}
                    <Collapse in={expandedRow === chamado.id} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 2, pb: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                        <Stack spacing={2} sx={{ pt: 1.5 }}>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>Descrição:</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{chamado.descricao || '-'}</Typography>
                          </Box>
                          {chamado.observacoes && (
                            <Box>
                              <Typography variant="subtitle2" gutterBottom>Observações:</Typography>
                              <Typography variant="body2">{chamado.observacoes}</Typography>
                            </Box>
                          )}
                          {chamado.observacao_reabertura && (
                            <Paper sx={{ p: 1.5, bgcolor: isManutencao ? '#fce4ec' : '#fff3e0', borderRadius: 1 }}>
                              <Typography variant="subtitle2" sx={{ color: isManutencao ? '#c62828' : '#e65100', fontWeight: 'bold' }}>⚠️ Observação de Reabertura:</Typography>
                              <Typography variant="body2">{chamado.observacao_reabertura}</Typography>
                            </Paper>
                          )}
                          <Stack spacing={0.5}>
                            <Typography variant="caption">
                              Criado por: {resolveChamadoCreatorName(chamado, usuariosById)}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ChatBubbleOutline />}
                              onClick={() => handleOpenChatModal(chamado)}
                              sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                            >
                              Chat ({Array.isArray(chamado.comentarios_internos) ? chamado.comentarios_internos.length : 0})
                              {getComentariosNaoLidosCount(chamado) > 0
                                ? ` • ${getComentariosNaoLidosCount(chamado)} novo(s)`
                                : ''}
                            </Button>
                            {chamado.atendente && <Typography variant="caption" color="warning.main">Em atendimento por: <strong>{chamado.atendente}</strong></Typography>}
                            {chamado.data_fechamento && <Typography variant="caption">Fechado em: {formatDate(chamado.data_fechamento)}</Typography>}
                            {chamado.pendencia && (
                              <Paper sx={{ p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                                <Typography variant="caption" color="error.contrastText" fontWeight="bold">Pendência: {chamado.pendencia}</Typography>
                              </Paper>
                            )}
                            {hasValidCoords(chamado.latitude, chamado.longitude) && (
                              <Chip icon={<LocationOn />} label={`${formatCoord(chamado.latitude)}, ${formatCoord(chamado.longitude)}`} size="small" variant="outlined" sx={{ alignSelf: 'flex-start' }} onClick={() => window.open(`https://maps.google.com/?q=${chamado.latitude},${chamado.longitude}`, '_blank')} />
                            )}
                            {chamado.planta_id && (
                              chamado.planta_arquivada ? (
                                <Chip icon={<Archive />} label={`Planta Arquivada (${chamado.planta_nivel_display || ''})`} size="small" color="warning" variant="outlined" sx={{ alignSelf: 'flex-start', cursor: 'pointer' }} onClick={() => handleOpenPlantaDialog(chamado)} />
                              ) : (
                                <Chip icon={<Place />} label={chamado.planta_nivel_display ? `Ver na Planta — ${chamado.planta_nivel_display}` : 'Ver na Planta'} size="small" color="info" variant="outlined" sx={{ alignSelf: 'flex-start', cursor: 'pointer' }} onClick={() => handleOpenPlantaDialog(chamado)} />
                              )
                            )}
                          </Stack>
                          {hasClosingReportContent(chamado.relatorio_fechamento) && (
                            <ChamadoClosingReportSection report={chamado.relatorio_fechamento} />
                          )}
                          {/* Botões de atendimento */}
                          {!isManutencao && (
                            <Stack spacing={1}>
                              {chamado.status === 'aberto' && <Button fullWidth variant="contained" color="warning" size="small" startIcon={<PlayCircle />} onClick={() => handleIniciarAtendimento(chamado.id)}>Iniciar Atendimento</Button>}
                              {chamado.status === 'em_andamento' && (
                                <>
                                  <Chip label={`Em andamento: ${chamado.atendente || 'Usuário'}`} color="warning" size="small" />
                                  <Button fullWidth variant="contained" color="success" size="small" startIcon={<CheckCircle />} onClick={() => handleTratado(chamado.id)}>Tratado</Button>
                                  <Button fullWidth variant="outlined" size="small" startIcon={<Pause />} onClick={() => handleOpenPausaModal(chamado.id)} sx={{ borderColor: '#9e9e9e', color: '#616161' }}>Pausar Chamado</Button>
                                  <Button fullWidth variant="contained" color="error" size="small" startIcon={<Warning />} onClick={() => handleOpenFechamentoModal(chamado, 'quick')}>Fechar com Relatório</Button>
                                </>
                              )}
                              {chamado.status === 'pausado' && (
                                <>
                                  <Chip label={`Pausado${chamado.atendente ? ': ' + chamado.atendente : ''}`} size="small" sx={{ bgcolor: '#9e9e9e', color: '#fff' }} />
                                  <Button fullWidth variant="contained" color="warning" size="small" startIcon={<PlayArrow />} onClick={() => handleRetomarChamado(chamado.id)}>Retomar Chamado</Button>
                                </>
                              )}
                              {chamado.status === 'resolvido' && <Chip label="Tratado" color="success" size="small" icon={<CheckCircle />} sx={{ alignSelf: 'flex-start' }} />}
                              {chamado.status === 'fechado' && <Tooltip title={chamado.pendencia || 'Sem comentário'}><Chip label="Chamado Encerrado" color="error" size="small" icon={<Warning />} sx={{ bgcolor: 'error.main', color: '#fff', alignSelf: 'flex-start', '& .MuiChip-icon': { color: '#fff' } }} /></Tooltip>}
                            </Stack>
                          )}
                          {isManutencao && (
                            <Stack spacing={1}>
                              {chamado.status === 'aberto' && <Button fullWidth variant="contained" color="success" size="small" startIcon={<ThumbUp />} onClick={() => handleOkManutencao(chamado.id)}>Ok - Iniciar Atendimento</Button>}
                              {chamado.status === 'em_andamento' && (
                                <>
                                  <Chip label={`Em andamento: ${chamado.atendente || 'Usuário'}`} color="warning" size="small" />
                                  <Button fullWidth variant="contained" color="success" size="small" startIcon={<CheckCircle />} onClick={() => handleResolvidoManutencao(chamado.id)}>Resolvido</Button>
                                  <Button fullWidth variant="outlined" size="small" startIcon={<Pause />} onClick={() => handleOpenPausaModal(chamado.id)} sx={{ borderColor: '#9e9e9e', color: '#616161' }}>Pausar Chamado</Button>
                                </>
                              )}
                              {chamado.status === 'pausado' && (
                                <>
                                  <Chip label={`Pausado${chamado.atendente ? ': ' + chamado.atendente : ''}`} size="small" sx={{ bgcolor: '#9e9e9e', color: '#fff' }} />
                                  <Button fullWidth variant="contained" color="warning" size="small" startIcon={<PlayArrow />} onClick={() => handleRetomarChamado(chamado.id)}>Retomar Chamado</Button>
                                </>
                              )}
                              {chamado.status === 'resolvido' && <Chip label="Tratado" color="success" size="small" icon={<CheckCircle />} sx={{ alignSelf: 'flex-start' }} />}
                              {chamado.status === 'fechado' && <Tooltip title={chamado.pendencia || 'Sem comentário'}><Chip label="Chamado Encerrado" color="error" size="small" icon={<Warning />} sx={{ bgcolor: 'error.main', color: '#fff', alignSelf: 'flex-start', '& .MuiChip-icon': { color: '#fff' } }} /></Tooltip>}
                            </Stack>
                          )}
                          {/* Histórico de Pausas */}
                          {chamado.pausas && chamado.pausas.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Pause fontSize="small" /> Histórico de Pausas ({chamado.pausas.length}):</Typography>
                              <Stack spacing={0.5}>
                                {chamado.pausas.map((p, i) => (
                                  <Paper key={i} sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                    <Typography variant="caption" fontWeight="bold">Pausa {i + 1} — {p.pausado_por || 'Usuário'}</Typography>
                                    <Typography variant="caption" display="block">Motivo: {p.motivo}</Typography>
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      Pausado em: {p.pausado_em ? new Date(p.pausado_em).toLocaleString('pt-BR') : '-'}
                                      {p.retomado_em ? ` | Retomado em: ${new Date(p.retomado_em).toLocaleString('pt-BR')}` : ' | Ainda pausado'}
                                    </Typography>
                                  </Paper>
                                ))}
                              </Stack>
                            </Box>
                          )}
                          {/* Fotos */}
                          {chamado.fotos && chamado.fotos.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" gutterBottom>Fotos:</Typography>
                              <Grid container spacing={1}>
                                {chamado.fotos.map((url, i) => (
                                  <Grid item xs={6} sm={4} key={i}>
                                    <Card sx={{ cursor: 'pointer' }} onClick={() => window.open(url, '_blank')}>
                                      <CardMedia component="img" height="90" image={url} sx={{ objectFit: 'cover' }} />
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}
                          {/* Documentos */}
                          {chamado.documentos && chamado.documentos.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" gutterBottom>Documentos:</Typography>
                              <Stack spacing={0.5}>
                                {chamado.documentos.map((doc, i) => (
                                  <Chip key={i} label={doc.name || `Documento ${i + 1}`} icon={<Download />} variant="outlined" size="small" onClick={() => window.open(doc.url || doc, '_blank')} sx={{ justifyContent: 'flex-start' }} />
                                ))}
                              </Stack>
                            </Box>
                          )}
                          {/* Áudio */}
                          {chamado.audio_url && (
                            <Box>
                              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Mic fontSize="small" /> Áudio Gravado:</Typography>
                              <audio controls src={chamado.audio_url} style={{ width: '100%', height: 40 }} />
                            </Box>
                          )}
                          {/* Vídeos */}
                          {chamado.videos && chamado.videos.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Videocam fontSize="small" /> Vídeos:</Typography>
                              <Stack spacing={1}>
                                {chamado.videos.map((url, i) => (
                                  <video key={i} src={url} controls style={{ width: '100%', borderRadius: 4 }} />
                                ))}
                              </Stack>
                            </Box>
                          )}
                          <WhatsappChamadoMediaSection chamado={chamado} compact />
                        </Stack>
                      </Box>
                    </Collapse>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <TablePagination
              component="div"
              count={chamadosFiltrados.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
          </>
        )}

        {/* Tab 1: Ocorrências (admin only) */}
        {isAdmin && mainTabIndex === 1 && (
          <OcorrenciasChamadosTab token={token} />
        )}
      </DialogContent>

      {/* Modal de Visualização */}
      <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Detalhes do Chamado #{chamadoDetalhes?.id}
          </Typography>
          <IconButton onClick={() => setViewModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {chamadoDetalhes && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Título</Typography>
                <Typography variant="body1">{chamadoDetalhes.titulo}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Cliente</Typography>
                <Typography variant="body1">{getClienteNome(chamadoDetalhes.cliente_id)}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                {getStatusChip(chamadoDetalhes.status, chamadoDetalhes)}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Prioridade</Typography>
                {getPrioridadeChip(chamadoDetalhes.prioridade)}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Tipo</Typography>
                <Typography variant="body1">{getTipoLabel(chamadoDetalhes.tipo)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Descrição</Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {chamadoDetalhes.descricao || '-'}
                  </Typography>
                </Paper>
              </Grid>
              {chamadoDetalhes.observacoes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Observações</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">{chamadoDetalhes.observacoes}</Typography>
                  </Paper>
                </Grid>
              )}
              {chamadoDetalhes.observacao_reabertura && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: isManutencao ? '#c62828' : '#e65100', fontWeight: 'bold' }}>
                    ⚠️ Observação de Reabertura
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: isManutencao ? '#fce4ec' : '#fff3e0', border: isManutencao ? '1px solid #f48fb1' : 'none' }}>
                    <Typography variant="body2" sx={{ fontWeight: isManutencao ? 600 : 400 }}>
                      {chamadoDetalhes.observacao_reabertura}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {chamadoDetalhes.atendente && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Atendente</Typography>
                  <Typography variant="body1" color="warning.main">{chamadoDetalhes.atendente}</Typography>
                </Grid>
              )}
              {chamadoDetalhes.pendencia && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Motivo da Pendência</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                    <Typography variant="body2" color="error.contrastText">
                      {chamadoDetalhes.pendencia}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {hasClosingReportContent(chamadoDetalhes.relatorio_fechamento) && (
                <Grid item xs={12}>
                  <ChamadoClosingReportSection report={chamadoDetalhes.relatorio_fechamento} />
                </Grid>
              )}
              {chamadoDetalhes.pausas && chamadoDetalhes.pausas.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Histórico de Pausas ({chamadoDetalhes.pausas.length})</Typography>
                  <Stack spacing={1}>
                    {chamadoDetalhes.pausas.map((p, i) => (
                      <Paper key={i} sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold">Pausa {i + 1} — {p.pausado_por || 'Usuário'}</Typography>
                        <Typography variant="body2">Motivo: {p.motivo}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Pausado em: {p.pausado_em ? new Date(p.pausado_em).toLocaleString('pt-BR') : '-'}
                          {p.retomado_em ? ` | Retomado em: ${new Date(p.retomado_em).toLocaleString('pt-BR')}` : ' | Ainda pausado'}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Data de Abertura</Typography>
                <Typography variant="body1">{formatDateTime(chamadoDetalhes.data_abertura)}</Typography>
              </Grid>
              {chamadoDetalhes.data_fechamento && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Data de Fechamento</Typography>
                  <Typography variant="body1">{formatDateTime(chamadoDetalhes.data_fechamento)}</Typography>
                </Grid>
              )}
              {chamadoDetalhes.prazo_conclusao && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Prazo para Conclusão</Typography>
                  <Typography variant="body1" sx={{ color: new Date(chamadoDetalhes.prazo_conclusao + 'T23:59:59') < new Date() && !['resolvido','fechado'].includes(chamadoDetalhes.status) ? 'error.main' : 'text.primary', fontWeight: new Date(chamadoDetalhes.prazo_conclusao + 'T23:59:59') < new Date() && !['resolvido','fechado'].includes(chamadoDetalhes.status) ? 700 : 400 }}>
                    {new Date(chamadoDetalhes.prazo_conclusao + 'T00:00:00').toLocaleDateString('pt-BR')}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Criado por</Typography>
                <Typography variant="body1">
                  {resolveChamadoCreatorName(chamadoDetalhes, usuariosById)}
                </Typography>
              </Grid>
              {calcTempoChamado(chamadoDetalhes) && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: ['resolvido', 'fechado'].includes(chamadoDetalhes.status) ? '#e8f5e9' : '#fff3e0', borderRadius: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      ⏱️ {calcTempoChamado(chamadoDetalhes)}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Áudio */}
              {chamadoDetalhes.audio_url && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Áudio Gravado
                  </Typography>
                  <audio controls src={chamadoDetalhes.audio_url} style={{ width: '100%' }} />
                  {chamadoDetalhes.transcricao && (
                    <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                      <Typography variant="caption" color="text.secondary">Transcrição:</Typography>
                      <Typography variant="body2">{chamadoDetalhes.transcricao}</Typography>
                    </Paper>
                  )}
                </Grid>
              )}

              {/* Posição na Planta */}
              {chamadoDetalhes.planta_x != null && chamadoDetalhes.planta_y != null && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Posição na Planta Baixa
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<MapIcon />}
                    size="small"
                    onClick={() => handleOpenPlantaDialog(chamadoDetalhes)}
                  >
                    Ver na Planta
                  </Button>
                </Grid>
              )}

              {/* Fotos */}
              {chamadoDetalhes.fotos && chamadoDetalhes.fotos.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Fotos</Typography>
                  <Grid container spacing={1}>
                    {chamadoDetalhes.fotos.map((url, i) => (
                      <Grid item xs={4} sm={3} md={2} key={i}>
                        <Card sx={{ cursor: 'pointer' }} onClick={() => window.open(url, '_blank')}>
                          <CardMedia component="img" height="80" image={url} sx={{ objectFit: 'cover' }} />
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}

              {/* Vídeos */}
              {chamadoDetalhes.videos && chamadoDetalhes.videos.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Vídeos</Typography>
                  <Grid container spacing={1}>
                    {chamadoDetalhes.videos.map((url, i) => (
                      <Grid item xs={6} sm={4} key={i}>
                        <video src={url} controls style={{ width: '100%', borderRadius: 4 }} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}

              {/* Documentos */}
              {chamadoDetalhes.documentos && chamadoDetalhes.documentos.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Documentos</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {chamadoDetalhes.documentos.map((doc, i) => (
                      <Chip
                        key={i}
                        label={doc.name || `Documento ${i + 1}`}
                        icon={<Download />}
                        variant="outlined"
                        onClick={() => window.open(doc.url || doc, '_blank')}
                      />
                    ))}
                  </Stack>
                </Grid>
              )}

              {getChamadoWhatsappMediaList(chamadoDetalhes).length > 0 && (
                <Grid item xs={12}>
                  <WhatsappChamadoMediaSection chamado={chamadoDetalhes} />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewModalOpen(false)}>Fechar</Button>
          {isManutencao && chamadoDetalhes?.status === 'aberto' && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ThumbUp />}
              onClick={() => {
                setViewModalOpen(false);
                handleOkManutencao(chamadoDetalhes.id);
              }}
            >
              Ok - Em Andamento
            </Button>
          )}
          {isManutencao && chamadoDetalhes?.status === 'em_andamento' && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => {
                setViewModalOpen(false);
                handleResolvidoManutencao(chamadoDetalhes.id);
              }}
            >
              Resolvido
            </Button>
          )}
          {chamadoDetalhes?.status === 'em_andamento' && (
            <Button
              variant="outlined"
              startIcon={<Pause />}
              sx={{ borderColor: '#9e9e9e', color: '#616161' }}
              onClick={() => {
                setViewModalOpen(false);
                handleOpenPausaModal(chamadoDetalhes.id);
              }}
            >
              Pausar
            </Button>
          )}
          {chamadoDetalhes?.status === 'pausado' && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<PlayArrow />}
              onClick={() => {
                setViewModalOpen(false);
                handleRetomarChamado(chamadoDetalhes.id);
              }}
            >
              Retomar
            </Button>
          )}
          <Button 
            variant="contained" 
            onClick={() => {
              setViewModalOpen(false);
              handleEdit(chamadoDetalhes);
            }}
          >
            {isManutencao && (chamadoDetalhes?.tipo || '').toLowerCase() !== 'emergencia' ? 'Adicionar mídias' : 'Editar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {isManutencao && !isEditandoEmergencia
              ? `Adicionar mídias ao Chamado #${chamadoEditando?.id}`
              : `Editar Chamado #${chamadoEditando?.id}`}
          </Typography>
          <IconButton onClick={() => setEditModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {chamadoEditando && (
            <Grid container spacing={2}>
              {isManutencao && (
                <Grid item xs={12}>
                  <Alert severity={isEditandoEmergencia ? 'success' : 'info'}>
                    {isEditandoEmergencia
                      ? 'Emergência liberada para o perfil Manutenção: é possível responder, alterar status e anexar mídias.'
                      : 'Neste modo, o perfil Manutenção pode apenas anexar novas fotos, vídeos e documentos ao chamado.'}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Título"
                  value={chamadoEditando.titulo || ''}
                  onChange={(e) => setChamadoEditando({ ...chamadoEditando, titulo: e.target.value })}
                  disabled={!canEditChamadoFields}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Cliente"
                  value={chamadoEditando.cliente_id || ''}
                  onChange={async (e) => {
                    const novoClienteId = e.target.value;
                    setChamadoEditando({ ...chamadoEditando, cliente_id: novoClienteId, planta_id: '' });
                    setEditPlantasList([]);
                    if (novoClienteId) {
                      try {
                        const res = await fetch(`${API}/clientes/${novoClienteId}/planta_chamados`, { headers });
                        if (res.ok) {
                          const data = await res.json();
                          setEditPlantasList((data.plantas || []).filter((p) => !p.arquivada));
                        }
                      } catch (err) {
                        console.error('Erro ao carregar plantas:', err);
                      }
                    }
                  }}
                  disabled={!canEditChamadoFields}
                >
                  {clientes.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={chamadoEditando.status || ''}
                  onChange={(e) => handleEditStatusChange(e.target.value)}
                  disabled={!canEditChamadoFields}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Prioridade"
                  value={chamadoEditando.prioridade || ''}
                  onChange={(e) => setChamadoEditando({ ...chamadoEditando, prioridade: e.target.value })}
                  disabled={!canEditChamadoFields}
                >
                  {PRIORIDADE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Tipo"
                  value={chamadoEditando.tipo || ''}
                  onChange={(e) => setChamadoEditando({ ...chamadoEditando, tipo: e.target.value })}
                  disabled={!canEditChamadoFields}
                >
                  {TIPO_OPTIONS.filter((o) => !(isManutencao && o.value === 'projetos')).map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              {isAdmin && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Criado por"
                    value={chamadoEditando.criado_por ?? ''}
                    onChange={(e) => setChamadoEditando({
                      ...chamadoEditando,
                      criado_por: e.target.value === '' ? '' : Number(e.target.value),
                    })}
                    helperText="Use Admin quando o histórico antigo não permitir identificar o criador com segurança."
                    disabled={!canEditChamadoFields}
                  >
                    <MenuItem value="">Não definido</MenuItem>
                    {usuarios.map((usuario) => (
                      <MenuItem key={usuario.id} value={usuario.id}>
                        {usuario.nome}{usuario.username ? ` (${usuario.username})` : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              {/* Seletor de Planta */}
              {editPlantasList.length > 0 && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Planta Baixa"
                    value={chamadoEditando.planta_id || ''}
                    onChange={(e) => setChamadoEditando({ ...chamadoEditando, planta_id: e.target.value || '' })}
                    disabled={!canEditChamadoFields}
                  >
                    <MenuItem value="">Nenhuma</MenuItem>
                    {editPlantasList.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.nivel_display} {p.nome ? `— ${p.nome}` : ''}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prazo para Conclusão"
                  type="date"
                  value={chamadoEditando.prazo_conclusao || ''}
                  onChange={(e) => setChamadoEditando({ ...chamadoEditando, prazo_conclusao: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  disabled={!canEditChamadoFields}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Descrição"
                  value={chamadoEditando.descricao || ''}
                  onChange={(e) => setChamadoEditando({ ...chamadoEditando, descricao: e.target.value })}
                  disabled={!canEditChamadoFields}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Observações"
                  value={chamadoEditando.observacoes || ''}
                  onChange={(e) => setChamadoEditando({ ...chamadoEditando, observacoes: e.target.value })}
                  disabled={!canEditChamadoFields}
                />
              </Grid>

              {/* Seção de Arquivos (Fotos, Docs, Vídeos) */}
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
                  Anexos (Fotos, Vídeos, Documentos)
                </Typography>

                {/* Exibição dos Anexos Existentes */}
                {(() => {
                  let exFotos = [];
                  let exDocs = [];
                  let exVideos = [];
                  try { exFotos = typeof chamadoEditando.fotos === 'string' ? JSON.parse(chamadoEditando.fotos || '[]') : (chamadoEditando.fotos || []); } catch(e){}
                  try { exDocs = typeof chamadoEditando.documentos === 'string' ? JSON.parse(chamadoEditando.documentos || '[]') : (chamadoEditando.documentos || []); } catch(e){}
                  try { exVideos = typeof chamadoEditando.videos === 'string' ? JSON.parse(chamadoEditando.videos || '[]') : (chamadoEditando.videos || []); } catch(e){}
                  
                  // Filtrar os deletados
                  exFotos = exFotos.filter(url => !deletedFotos.includes(url));
                  exDocs = exDocs.filter(d => {
                    const url = typeof d === 'object' ? d.url : d;
                    return !deletedDocs.includes(url);
                  });
                  exVideos = exVideos.filter(url => !deletedVideos.includes(url));

                  if (exFotos.length === 0 && exDocs.length === 0 && exVideos.length === 0) return null;

                  return (
                    <Box sx={{ mb: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Anexos Atuais</Typography>
                      
                      {exFotos.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">Fotos:</Typography>
                          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {exFotos.map((url, i) => (
                              <ChamadoFotoThumb
                                key={`${url}-${i}`}
                                chamadoId={chamadoEditando.id}
                                url={url}
                                token={token}
                                onOpen={abrirFotoAmpliada}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {exVideos.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">Vídeos:</Typography>
                          <Stack spacing={0.5} sx={{ mt: 0.5 }} alignItems="flex-start">
                            {exVideos.map((url, i) => (
                              <Chip
                                key={i}
                                label={`Vídeo existente ${i+1}`}
                                onDelete={isAdmin ? () => setDeletedVideos(prev => [...prev, url]) : undefined}
                                size="small"
                                icon={<Videocam fontSize="small" />}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {exDocs.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Documentos:</Typography>
                          <Stack spacing={0.5} sx={{ mt: 0.5 }} alignItems="flex-start">
                            {exDocs.map((d, i) => {
                              const url = typeof d === 'object' ? d.url : d;
                              const name = typeof d === 'object' ? d.name : `Documento existente ${i+1}`;
                              return (
                                <Chip
                                  key={i}
                                  label={name}
                                  onDelete={isAdmin ? () => setDeletedDocs(prev => [...prev, url]) : undefined}
                                  size="small"
                                  icon={<Description fontSize="small" />}
                                />
                              )
                            })}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  );
                })()}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <Button variant="outlined" component="label" startIcon={<Photo />}>
                    Foto
                    <input type="file" hidden accept="image/*" onChange={handleEditFotoChange} />
                  </Button>
                  <Button variant="outlined" component="label" startIcon={<Videocam />}>
                    Vídeo
                    <input type="file" hidden accept="video/*" onChange={handleEditVideoChange} />
                  </Button>
                  <Button variant="outlined" component="label" startIcon={<Description />}>
                    Documento
                    <input type="file" hidden accept=".pdf,.doc,.docx,.xls,.xlsx" multiple onChange={handleEditDocChange} />
                  </Button>
                </Stack>

                {/* Previews das Fotos selecionadas */}
                {editFotos.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption">Fotos a adicionar:</Typography>
                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      {editFotos.map((f, i) => (
                        <Grid item xs={4} sm={3} md={2} key={i}>
                          <Card sx={{ position: 'relative' }}>
                            <CardMedia component="img" height="60" image={f.preview} sx={{ objectFit: 'cover' }} />
                            <IconButton
                              size="small"
                              onClick={() => removeEditFoto(i)}
                              sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(255,255,255,0.7)', p: 0.1 }}
                            >
                              <Close fontSize="small" color="error" />
                            </IconButton>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Previews de Vídeos selecionados */}
                {editVideos.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption">Vídeos a adicionar:</Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {editVideos.map((v, i) => (
                        <Chip
                          key={i}
                          label={v.name}
                          onDelete={() => removeEditVideo(i)}
                          size="small"
                          icon={<Videocam fontSize="small" />}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Previews de Documentos selecionados */}
                {editDocs.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption">Documentos a adicionar:</Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {editDocs.map((d, i) => (
                        <Chip
                          key={i}
                          label={d.name}
                          onDelete={() => removeEditDoc(i)}
                          size="small"
                          icon={<Description fontSize="small" />}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Grid>

              {/* Campo de Observação de Reabertura - obrigatório ao mudar status para aberto */}
              {chamadoEditando.status === 'aberto' && statusOriginalEdicao !== 'aberto' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Observação de Reabertura *"
                    value={chamadoEditando.observacao_reabertura || ''}
                    onChange={(e) => setChamadoEditando({ ...chamadoEditando, observacao_reabertura: e.target.value })}
                    placeholder="Descreva o motivo da reabertura do chamado..."
                    error={!(chamadoEditando.observacao_reabertura || '').trim()}
                    helperText={!(chamadoEditando.observacao_reabertura || '').trim() ? 'Campo obrigatório ao reabrir chamado' : ''}
                    sx={{ bgcolor: '#fff3e0' }}
                  />
                </Grid>
              )}
              {/* Campo de Motivo da Pausa - obrigatório ao mudar status para pausado */}
              {chamadoEditando.status === 'pausado' && statusOriginalEdicao !== 'pausado' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Motivo da Pausa *"
                    value={chamadoEditando.motivo_pausa || ''}
                    onChange={(e) => setChamadoEditando({ ...chamadoEditando, motivo_pausa: e.target.value })}
                    placeholder="Descreva o motivo da pausa do chamado..."
                    error={!(chamadoEditando.motivo_pausa || '').trim()}
                    helperText={!(chamadoEditando.motivo_pausa || '').trim() ? 'Campo obrigatório ao pausar chamado' : ''}
                    sx={{ bgcolor: '#fff3e0' }}
                  />
                </Grid>
              )}
                              {!isManutencao && chamadoEditando.status === 'fechado' && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: '#f8fafc', border: '1px dashed', borderColor: 'divider' }}>
                    <Typography variant="subtitle2">Relatório de Fechamento</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {hasClosingReportContent(chamadoEditando.relatorio_fechamento)
                        ? 'Relatório pronto para revisão, com assinatura em tela vinculada ao fechamento.'
                        : 'Preencha o relatório e capture a assinatura em tela para concluir o fechamento.'}
                    </Typography>
                    <Button
                      sx={{ mt: 1.5 }}
                      variant="outlined"
                      color="error"
                      startIcon={<Description />}
                      onClick={() => handleOpenFechamentoModal(chamadoEditando, 'edit')}
                    >
                      {hasClosingReportContent(chamadoEditando.relatorio_fechamento)
                        ? 'Editar relatório de fechamento'
                        : 'Preencher relatório de fechamento'}
                    </Button>
                  </Paper>
                </Grid>
              )}
              {chamadoEditando.pendencia && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Pendência"
                    value={chamadoEditando.pendencia || ''}
                    onChange={(e) => setChamadoEditando({ ...chamadoEditando, pendencia: e.target.value })}
                    sx={{ bgcolor: 'error.light' }}
                    disabled={!canEditChamadoFields}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveEdit}>
            {isManutencao && !isEditandoEmergencia ? 'Salvar mídias' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(fotoAmpliadaUrl)} onClose={fecharFotoAmpliada} fullWidth maxWidth="lg" PaperProps={{ sx: { bgcolor: '#111', overflow: 'hidden' } }}>
        <DialogContent
          sx={{
            p: 0,
            minHeight: { xs: '70vh', md: '82vh' },
            bgcolor: '#111',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {fotoAmpliadaSrc ? (
            <Box
              component="img"
              src={fotoAmpliadaSrc}
              alt="Foto ampliada"
              sx={{ maxWidth: '100%', maxHeight: { xs: '70vh', md: '82vh' }, objectFit: 'contain' }}
            />
          ) : (
            <Typography sx={{ color: '#fff' }}>Carregando foto...</Typography>
          )}

          {canDeleteFotos && (
            <IconButton
              aria-label="Apagar foto"
              title="Apagar foto"
              onClick={() => setFotoConfirmarExclusao(fotoAmpliadaUrl)}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 3,
                width: 46,
                height: 46,
                color: '#fff',
                bgcolor: 'error.main',
                border: '3px solid #fff',
                boxShadow: 5,
                '&:hover': { bgcolor: 'error.dark', transform: 'scale(1.06)' },
              }}
            >
              <Close sx={{ fontSize: 30 }} />
            </IconButton>
          )}

          <Button
            variant="contained"
            onClick={fecharFotoAmpliada}
            sx={{
              position: 'absolute',
              left: 12,
              bottom: 12,
              bgcolor: 'rgba(255,255,255,0.18)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
            }}
          >
            Fechar
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(fotoConfirmarExclusao)} onClose={() => setFotoConfirmarExclusao(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Tem certeza apagar a foto?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            A foto será removida ao salvar o chamado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFotoConfirmarExclusao(null)}>Não</Button>
          <Button variant="contained" color="error" onClick={confirmarApagarFoto}>
            Sim
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Fechamento com Relatório */}
      <Dialog open={fechamentoModalOpen} onClose={handleCloseFechamentoModal} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            Relatório de Fechamento do Chamado
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#e2e8f0' }}>
          {fechamentoMissingFields.length > 0 && (
            <Alert
              severity="warning"
              sx={{ maxWidth: 980, mx: 'auto', mb: 2 }}
              onClose={() => setFechamentoMissingFields([])}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Não é possível aplicar o fechamento ainda.
              </Typography>
              <Typography variant="body2">
                Preencha os campos obrigatórios: {fechamentoMissingFields.join(', ')}.
              </Typography>
            </Alert>
          )}
          <Box
            sx={{
              maxWidth: 980,
              mx: 'auto',
              bgcolor: '#fff',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 }, bgcolor: '#fff7ed', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  <Box>
                    <Typography variant="overline" sx={{ color: '#c2410c', fontWeight: 700 }}>
                      Documento de encerramento
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Relatório técnico de fechamento
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Preencha o registro final do chamado em uma única página, com aceite e assinatura capturada na tela.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Chip label={`Chamado #${fechamentoContext.chamado?.id || '-'}`} color="error" variant="outlined" />
                    <Chip
                      label={fechamentoContext.mode === 'edit' ? 'Aplicado no modo edição' : 'Fechamento imediato'}
                      color="warning"
                      variant="outlined"
                    />
                  </Stack>
                </Stack>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={3}>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff' }}>
                      <Typography variant="caption" color="text.secondary">Chamado</Typography>
                      <Typography variant="body1" fontWeight={700}>#{fechamentoContext.chamado?.id || '-'}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff' }}>
                      <Typography variant="caption" color="text.secondary">Cliente</Typography>
                      <Typography variant="body1">{fechamentoContext.chamado?.cliente_nome || '-'}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff' }}>
                      <Typography variant="caption" color="text.secondary">Abertura</Typography>
                      <Typography variant="body1">{formatDateTime(fechamentoContext.chamado?.data_abertura)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff' }}>
                      <Typography variant="caption" color="text.secondary">Título do chamado</Typography>
                      <Typography variant="body1">{fechamentoContext.chamado?.titulo || '-'}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Stack>
            </Box>

            <Stack spacing={3} sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
              <Box>
                <Typography variant="overline" sx={{ color: '#b45309', fontWeight: 700 }}>
                  1. Identificação do atendimento
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.25 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Responsável técnico *"
                      value={relatorioFechamentoDraft.tecnico_responsavel}
                      onChange={(e) => setRelatorioFechamentoDraft((prev) => ({ ...prev, tecnico_responsavel: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Acompanhou o serviço"
                      value={relatorioFechamentoDraft.acompanhou_servico}
                      onChange={(e) => setRelatorioFechamentoDraft((prev) => ({ ...prev, acompanhou_servico: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Local do atendimento"
                      value={relatorioFechamentoDraft.local_atendimento}
                      onChange={(e) => setRelatorioFechamentoDraft((prev) => ({ ...prev, local_atendimento: e.target.value }))}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="overline" sx={{ color: '#b45309', fontWeight: 700 }}>
                  2. Relato e execução
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.25 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Problema reportado"
                      value={relatorioFechamentoDraft.problema_reportado}
                      onChange={(e) => setRelatorioFechamentoDraft((prev) => ({ ...prev, problema_reportado: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={5}
                      label="Diagnóstico realizado *"
                      value={relatorioFechamentoDraft.diagnostico}
                      onChange={(e) => setRelatorioFechamentoDraft((prev) => ({ ...prev, diagnostico: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={5}
                      label="Serviço executado *"
                      value={relatorioFechamentoDraft.servico_executado}
                      onChange={(e) => setRelatorioFechamentoDraft((prev) => ({ ...prev, servico_executado: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Materiais utilizados"
                      value={relatorioFechamentoDraft.materiais_utilizados}
                      onChange={(e) => setRelatorioFechamentoDraft((prev) => ({ ...prev, materiais_utilizados: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Orientações ao cliente"
                      value={relatorioFechamentoDraft.orientacoes_cliente}
                      onChange={(e) => setRelatorioFechamentoDraft((prev) => ({ ...prev, orientacoes_cliente: e.target.value }))}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="overline" sx={{ color: '#b45309', fontWeight: 700 }}>
                  3. Encerramento e pendências
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.25 }}>
                  <Grid item xs={12} md={7}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Pendências e observações finais"
                          value={relatorioFechamentoDraft.pendencias_observacoes}
                          onChange={(e) =>
                            setRelatorioFechamentoDraft((prev) => ({
                              ...prev,
                              pendencias_observacoes: e.target.value,
                              retorno_necessario: prev.retorno_necessario || Boolean(e.target.value.trim()),
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Conclusão do atendimento *"
                          value={relatorioFechamentoDraft.conclusao}
                          onChange={(e) => setRelatorioFechamentoDraft((prev) => ({ ...prev, conclusao: e.target.value }))}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: '#f8fafc' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Validação rápida do fechamento
                      </Typography>
                      <Stack spacing={1}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={relatorioFechamentoDraft.equipamento_liberado}
                              onChange={(e) =>
                                setRelatorioFechamentoDraft((prev) => ({
                                  ...prev,
                                  equipamento_liberado: e.target.checked,
                                }))
                              }
                            />
                          }
                          label="Equipamento liberado"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={relatorioFechamentoDraft.retorno_necessario}
                              onChange={(e) =>
                                setRelatorioFechamentoDraft((prev) => ({
                                  ...prev,
                                  retorno_necessario: e.target.checked,
                                }))
                              }
                            />
                          }
                          label="Retorno necessário"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={relatorioFechamentoDraft.cliente_ciente}
                              onChange={(e) =>
                                setRelatorioFechamentoDraft((prev) => ({
                                  ...prev,
                                  cliente_ciente: e.target.checked,
                                }))
                              }
                            />
                          }
                          label="Cliente ciente do atendimento"
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        Use este bloco para marcar se o equipamento foi liberado, se haverá retorno e se o responsável no local validou o encerramento.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="overline" sx={{ color: '#b45309', fontWeight: 700 }}>
                  4. Assinatura e aceite
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.25 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nome do assinante *"
                      value={relatorioFechamentoDraft.nome_assinante}
                      onChange={(e) => setRelatorioFechamentoDraft((prev) => ({ ...prev, nome_assinante: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Cargo/Função"
                      value={relatorioFechamentoDraft.cargo_assinante}
                      onChange={(e) => setRelatorioFechamentoDraft((prev) => ({ ...prev, cargo_assinante: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SignaturePadField
                      value={relatorioFechamentoDraft.assinatura}
                      onChange={(assinatura) => setRelatorioFechamentoDraft((prev) => ({ ...prev, assinatura }))}
                      label="Assinatura na tela *"
                      helperText="O assinante pode usar mouse, touchpad ou toque para registrar o aceite diretamente nesta folha de fechamento."
                    />
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFechamentoModal} disabled={fechamentoSubmitting}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmarFechamento}
            disabled={fechamentoSubmitting}
          >
            {fechamentoSubmitting
              ? 'Aplicando...'
              : fechamentoContext.mode === 'edit'
              ? 'Aplicar ao Chamado'
              : 'Fechar Chamado'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Pausa */}
      <Dialog open={pausaModalOpen} onClose={() => setPausaModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#9e9e9e', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Pause />
            Pausar Chamado
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Descreva o motivo pelo qual o chamado está sendo pausado. Esta informação é obrigatória.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Motivo da Pausa *"
            value={pausaMotivo}
            onChange={(e) => setPausaMotivo(e.target.value)}
            placeholder="Descreva o motivo da pausa..."
            error={!pausaMotivo.trim()}
            helperText={!pausaMotivo.trim() ? 'Campo obrigatório' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPausaModalOpen(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            sx={{ bgcolor: '#616161', '&:hover': { bgcolor: '#424242' } }}
            onClick={handleConfirmarPausa}
            disabled={!pausaMotivo.trim()}
          >
            Confirmar Pausa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={emergenciaPopupOpen}
        onClose={handleFecharPopupEmergencia}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#b91c1c', color: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            Emergência pendente para tratamento
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {emergenciaPendente ? (
            <Stack spacing={1.5}>
              <Alert severity="error">
                Existe um chamado de emergência pendente e ele precisa de atendimento imediato.
              </Alert>
              <Typography variant="body2">
                <strong>Chamado:</strong> #{emergenciaPendente.id}
              </Typography>
              <Typography variant="body2">
                <strong>Título:</strong> {emergenciaPendente.titulo || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Cliente:</strong> {getClienteNome(emergenciaPendente.cliente_id)}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {getStatusLabel(emergenciaPendente.status)}
              </Typography>
              <Typography variant="body2">
                <strong>Prioridade:</strong> {getPrioridadeLabel(emergenciaPendente.prioridade)}
              </Typography>
              {emergenciaPendente.data_abertura && (
                <Typography variant="body2">
                  <strong>Abertura:</strong> {formatDateTime(emergenciaPendente.data_abertura)}
                </Typography>
              )}
            </Stack>
          ) : (
            <Alert severity="info">Nenhuma emergência pendente no momento.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharPopupEmergencia}>Lembrar depois</Button>
          <Button variant="contained" color="error" onClick={handleTratarEmergenciaAgora}>
            Tratar agora
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={chatModalOpen} onClose={handleCloseChatModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatBubbleOutline color="info" />
            <Typography variant="h6">
              Chat do Chamado #{chatChamado?.id || '-'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseChatModal} disabled={chatSubmitting}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box
              sx={{
                maxHeight: 360,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                pr: 0.5,
              }}
            >
              {(Array.isArray(chatChamado?.comentarios_internos) ? chatChamado.comentarios_internos : []).length === 0 ? (
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhum comentário registrado para este chamado.
                  </Typography>
                </Paper>
              ) : (
                (Array.isArray(chatChamado?.comentarios_internos) ? chatChamado.comentarios_internos : []).map((comentario) => (
                  <Paper key={comentario.id || `${comentario.autor_nome}-${comentario.criado_em}`} sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {comentario.autor_nome || 'Usuário'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCommentDateTime(comentario.criado_em)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {comentario.texto || '-'}
                    </Typography>
                  </Paper>
                ))
              )}
            </Box>

            <TextField
              label="Novo comentário"
              placeholder="Digite uma atualização do chamado"
              multiline
              minRows={4}
              value={novoComentarioChat}
              onChange={(e) => setNovoComentarioChat(e.target.value)}
              fullWidth
              disabled={chatSubmitting}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChatModal} disabled={chatSubmitting}>Fechar</Button>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={handleSalvarComentarioChat}
            disabled={chatSubmitting || !novoComentarioChat.trim()}
          >
            {chatSubmitting ? 'Salvando...' : 'Registrar comentário'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Planta Baixa - Multi-plantas */}
      <Dialog open={plantaDialogOpen} onClose={() => setPlantaDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapIcon /> Planta Baixa — Chamado #{plantaDialogData.chamadoId}
          </Typography>
          <IconButton onClick={() => setPlantaDialogOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Alerta de planta arquivada */}
          {plantaDialogData.plantaArquivada && (
            <Alert
              severity="warning"
              icon={<Archive />}
              sx={{ mb: 2 }}
              action={
                plantaDialogData.plantas.length > 0 ? (
                  <Button
                    color="warning"
                    size="small"
                    startIcon={<Update />}
                    onClick={() => {
                      const mesmoNivel = plantaDialogData.plantas.find(
                        (p) => p.nivel === plantaDialogData.plantaArquivada.nivel
                      );
                      const novaPlanta = mesmoNivel || plantaDialogData.plantas[0];
                      if (novaPlanta && window.confirm(
                        `Deseja atualizar este chamado para a planta ativa "${novaPlanta.nivel_display}"?`
                      )) {
                        handleAtualizarPlantaChamado(novaPlanta.id);
                      }
                    }}
                  >
                    Atualizar Planta
                  </Button>
                ) : null
              }
            >
              <strong>Planta Arquivada — {plantaDialogData.plantaArquivada.nivel_display}</strong>
              <br />
              Arquivada em: {plantaDialogData.plantaArquivada.arquivada_em || '—'}
              <br />
              <Typography variant="caption" color="text.secondary">
                Deseja atualizar para a planta atual?
              </Typography>
            </Alert>
          )}
          {plantaDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : plantaDialogData.plantas && plantaDialogData.plantas.length > 1 ? (
            <>
              <Tabs
                value={plantaDialogSelectedId || false}
                onChange={(_, v) => {
                  setPlantaDialogSelectedId(v);
                  const p = plantaDialogData.plantas.find((pl) => pl.id === v);
                  if (p) {
                    setPlantaDialogData((prev) => ({
                      ...prev,
                      imagem: p.planta_imagem,
                      chamados: p.chamados || [],
                    }));
                  }
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}
              >
                {plantaDialogData.plantas.map((p) => (
                  <Tab key={p.id} value={p.id} label={p.nivel_display} />
                ))}
              </Tabs>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Clique para reposicionar o marcador deste chamado
              </Typography>
              <PlantaViewer
                imagemUrl={plantaDialogData.imagem}
                chamados={plantaDialogData.chamados.filter(c => c.id !== plantaDialogData.chamadoId)}
                markerPosition={plantaEditPosition}
                selectedChamadoId={plantaDialogData.chamadoId}
                editable
                onClick={(x, y) => setPlantaEditPosition({ x, y })}
                height={500}
              />
            </>
          ) : plantaDialogData.imagem ? (
            <>
              {plantaDialogData.plantas?.length === 1 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Planta: <strong>{plantaDialogData.plantas[0].nivel_display}</strong>
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Clique para reposicionar o marcador deste chamado
              </Typography>
              <PlantaViewer
                imagemUrl={plantaDialogData.imagem}
                chamados={plantaDialogData.chamados.filter(c => c.id !== plantaDialogData.chamadoId)}
                markerPosition={plantaEditPosition}
                selectedChamadoId={plantaDialogData.chamadoId}
                editable
                onClick={(x, y) => setPlantaEditPosition({ x, y })}
                height={500}
              />
            </>
          ) : (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Este cliente não possui planta cadastrada.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlantaDialogOpen(false)}>Cancelar</Button>
          {plantaDialogData.imagem && (
            <Button variant="contained" onClick={handleSavePlantaPosition} disabled={!plantaEditPosition}>
              Salvar Posição
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
